/**
 * Pooja Steps Data Configuration
 *
 * This file contains all the data structures and content for different types of Hindu poojas.
 * It supports multiple languages (Hindi, English) and provides step-by-step
 * instructions for various religious ceremonies.
 */
// ============================================================================
// RUDRA ABHISHEK POOJA STEPS
// ============================================================================
export const rudraAbhishekSteps = [
    {
        id: 1,
        title: {
            hi: "गुरु ध्यान",
            en: "Guru Meditation (Guru Dhyan)",
        },
        description: {
            hi: "भक्तों, जैसे हम हर शुभ कार्य की शुरुआत माता-पिता के आशीर्वाद से करते हैं, वैसे ही इस पूजन की शुरुआत हम गुरु ध्यान और भगवान शिव के मंगलाचरण से करेंगे।" + "\n\n**निर्देश:**\n**कृपया आप अपने दोनों हाथों को जोड़कर और आंखे बंद करके गुरु का ध्यान कीजिए।**",
            en: "Just as, dear devotees, we begin every important task with the blessings of our parents, in the same way, we shall begin this sacred worship by meditating upon our Guru and offering auspicious invocation (Mangalacharan) to Lord Shiva." + "\n\n**Instructions:**\n**Please join your hands together, close your eyes, and focus your mind in deep reverence as we begin by meditating on our Guru.**",
        },
        mantras: [{
                mantra: { hi: "गुरुर्ब्रह्मा गुरुर्विष्णुः गुरुर्देवो महेश्वरः । गुरुः साक्षात् परं ब्रह्म तस्मै श्रीगुरवे नमः ॥",
                    en: "Guru Brahma, Guru Vishnu, Guru Devo Maheshwarah Guru Sakshat Param Brahma, Tasmai Shri Gurave Namah." },
                meaning: { hi: "गुरु ही ब्रह्मा, विष्णु और शिव का स्वरूप हैं — हम उन्हें प्रणाम करते हैं ताकि उनकी कृपा से पूजा सिद्ध हो।",
                    en: "The Guru is the embodiment of Brahma, Vishnu, and Shiva — we bow to him so that, by his grace, the worship may be successful.", }
            }]
    },
    {
        id: 2,
        title: {
            hi: "शिव ध्यान",
            en: "Shiva Meditation (Shiva Dhyan)",
        },
        description: {
            hi: "गुरू हमारे पूजन को सिद्ध करें, गुरु के ध्यान के बाद हम शिव जी का ध्यान जो की हमारे पूजन के प्रधान देव हैं -",
            en: "May our Guru bless and empower this worship, making it successful and complete. After meditating upon our Guru, we shall now focus our devotion on Lord Shiva, who is the presiding deity and the central divine force of this sacred ritual.",
        },
        mantras: [{
                mantra: { hi: "ॐ नमः शिवाय शान्ताय प्रसन्नाय स्वभाविने। नमस्ते शम्भु रूपाय नमस्ते परमात्मने॥",
                    en: "Om Namah Shivaya Shantaya Prasannaya Swabhavine, Namaste Shambhu Rupaya, Namaste Paramatmane.", },
                meaning: { hi: "शांतस्वभाव, प्रसन्नचित्त, शम्भुरूप और परमात्मा रूप भगवान शिव को मेरा नमस्कार है।",
                    en: "My salutations to Lord Shiva, who is calm-natured, cheerful, in the form of Shambhu, and the supreme soul.", }
            }]
    },
    {
        id: 3,
        title: {
            hi: "शुद्धिकरण",
            en: "Purification (Shuddhikaran)",
        },
        description: {
            hi: "अब हम शुद्धिकरण करेंगे, जिससे शरीर, मन और वातावरण पवित्र होता है और चित्त शुद्ध होता है ।" + "\n\n**निर्देश:**\n**भक्तों आप भी इस पूजन को बहुत ही शुद्ध – चित्त के साथ और भक्ति- भाव के साथ सुनें -**",
            en: "Now, we shall perform the purification (Shuddhikaran) ritual, through which the body, mind, and surroundings are sanctified, and the consciousness becomes pure." + "\n\n**Instructions:**\n**Dear devotees, you too are requested to listen to this worship with a pure heart and deep devotion and faith.**",
        },
        mantras: [{
                mantra: { hi: "ॐ अपवित्रः पवित्रो वा सर्वावस्थां गतोऽपि वा । यः स्मरेत् पुण्डरीकाक्षं स बाह्याभ्यन्तरः शुचिः ॥",
                    en: "Om Apavitrah Pavitro Va Sarvavasthām Gato’pi Va; Yaḥ Smaret Pundarikaksham Sa Bahyābhyantaraḥ Shuchih." },
                meaning: { hi: "ॐ! जो व्यक्ति अपवित्र हो या पवित्र, या जो किसी भी स्थिति में हो, यदि वह पुण्डरीकाक्ष (भगवान विष्णु/लक्ष्मी के नेत्र) का स्मरण करता है, तो वह व्यक्ति भीतरी और बाहरी रूप से शुद्ध हो जाता है।",
                    en: "Om! Whether a person is impure or pure, or in any state, if they remember Pundarikaaksha (the lotus-eyed one, Lord Vishnu/Lakshmi), that person becomes pure both internally and externally.", }
            }]
    },
    {
        id: 4,
        title: {
            hi: "आचमन",
            en: "Aachman (Sipping ritual for purification)"
        },
        description: {
            hi: "शुद्धिकरण के पश्चात् हम आचमन करेंगें, जिससे आंतरिक शुद्धि होती है।",
            en: "After the purification ritual, we shall perform Āchaman, which brings about inner purification and sanctity of the self."
        },
        mantras: [
            {
                mantra: {
                    hi: "ॐ केशवाय नमः । ॐ नारायणाय नमः । ॐ माधवाय नमः ॥ ॐ हृषिकेशाय नमः हस्त प्रक्षालन ",
                    en: "Om Keshavaya Namah, Om Narayanaya Namah, Om Madhavaya Namah, Om Hrishikesaya Namah, Hasta Praskhalan"
                },
                meaning: {
                    hi: "केशव को नमस्कार है, नारायण को नमस्कार है, माधव को नमस्कार है। जल ग्रहण कर यह स्मरण किया जाता है कि हम दिव्य और निर्मल ऊर्जा को अपने भीतर स्वीकार कर रहे हैं।",
                    en: "Salutations to Keshava, salutations to Narayana, salutations to Madhava. While taking water, it is remembered that we are accepting divine and pure energy within ourselves."
                }
            }
        ]
    },
    {
        id: 5,
        title: {
            hi: "आसन शुद्धि एवं पृथ्वी पूजन",
            en: "Seat Purification and Earth Worship (Aasan Shuddhi evam Prithvi Poojan)"
        },
        description: {
            hi: "अब जिस आसन पर हम बैठे हैं, उसकी शुद्धि और पृथ्वी माता का पूजन करेंगे। आसन शुद्धि अपने आसन की शुद्धि के लिए किया जाता है – और पृथ्वी पूजन जो पृथ्वी हम सबका भार धरे है उसको स्मरण करके किया जाता है।",
            en: "Now, we shall perform the purification of the seat (Āsan Shuddhi) on which we are seated, followed by the worship of Mother Earth (Prithvi Pujan). Āsan Shuddhi is done to purify and sanctify the seat we are sitting on, while Prithvi Pujan is performed to honor and remember Mother Earth, who graciously bears the weight of all living beings."
        },
        mantras: [
            {
                mantra: {
                    hi: "ॐ पृथ्व्यै नमः । पृथ्वी त्वया धृता लोका देवी त्वं विष्णुणा धृता त्वं च धारय मां भद्रे पवित्रं कुरु चासनम् । समुद्रवसने देवि पर्वतस्तनमण्डले । विष्णुपत्नि नमस्तुभ्यं पादस्पर्शं क्षमस्व मे ॥",
                    en: "Om Prithvyai Namah, Prithvi Tvaya Dhrita Loka Devi Tvam Vishnuna Dhrita Tvam Cha Dharaya Mam Bhadre Pavitram Kuru Asanam. Samudravasane Devi Parvatastana Mandale Vishnupatni Namastubhyam Padasparsham Kshamasva Me."
                },
                meaning: {
                    hi: "हे पृथ्वी देवी! आपने समस्त लोकों को धारण किया है और स्वयं भगवान विष्णु द्वारा धारण की गई हैं। हे कल्याणी माता! कृपया मुझे भी स्थिरता और संरक्षण प्रदान करें, और जिस आसन पर मैं बैठा हूँ उसे पवित्र बनाएं। पृथ्वी माता से क्षमा माँगते हैं क्योंकि उन पर बैठकर हम पूजा कर रहे हैं। हे देवी! आप समुद्र पर विराजमान और पर्वत पर निवास करने वाली हैं। आप विष्णु की पत्नी (श्रीमती लक्ष्मी) हैं। मैं आपको नमन करता हूँ और प्रार्थना करता हूँ कि मुझे आपके पादस्पर्श की कृपा प्राप्त हो।",
                    en: "O Earth Goddess! You support all the worlds and are yourself supported by Lord Vishnu. O auspicious Mother! Please grant me stability and protection, and purify the seat upon which I sit. I seek forgiveness from Mother Earth, as I sit upon her while performing this worship. O Goddess! You reside on the ocean and dwell on the mountains. You are the consort of Vishnu (Goddess Lakshmi). I bow to you and pray to receive the grace of your holy feet."
                }
            }
        ]
    },
    {
        id: 6,
        title: {
            hi: "दीप पूजन",
            en: "Lamp Worship (Deep Poojan)"
        },
        description: {
            hi: "अब हम दीप पूजन करेंगे जो की इस सम्पूर्ण पूजन के प्रधान और साक्षिभूत देव हैं जिनमें सदैव भगवान बटुक–भैरव जी का वास होता है जो कि न्याय के प्रिय देव हैं।",
            en: "Now, we shall perform the worship of the sacred lamp (Deep Pujan), which serves as the main and witnessing deity of this entire ritual. It is believed that Lord Batuk Bhairav, the divine embodiment of justice and protection, resides within the holy flame of the lamp. Hence, lighting and worshipping the lamp marks the beginning of auspiciousness and divine presence in the puja."
        },
        mantras: [
            {
                mantra: {
                    hi: "शुभं करोतु कल्याणं आरोग्यं धनसंपदाम् । शत्रुबुद्धिविनाशाय दीपज्योतिर्नमोऽस्तु ते ॥ गन्द्धाक्षातान् समर्पयामि ।",
                    en: "Shubham Karotu Kalyanam, Arogyam Dhanasampadam; Shatrubuddhivinashaya, Deepajyotir Namo’stu Te. Gandhakshatan Samarpayami."
                },
                meaning: {
                    hi: "हे दीप! आप शुभता, कल्याण, आरोग्य और धन-सम्पदा प्रदान करें। आप शत्रु और बुरी बुद्धियों का नाश करें। हे दीप! मैं आपको नमन करता हूँ।",
                    en: "O Lamp! You bestow auspiciousness, well-being, health, and wealth. You destroy enemies and evil intellects. O Lamp! I bow to you."
                }
            }
        ]
    },
    {
        id: 7,
        title: {
            hi: "स्वस्ति-वाचन",
            en: "Auspicious Invocation (Swasti Vachan)"
        },
        description: {
            hi: "स्वस्ति वाचन जो कल्याण, आशीर्वाद और सद्भाव के लिए शुभ प्रार्थनाओं या आह्वाहन के पाठ या जप को संदर्भित करता है। ये प्रार्थनाएँ हम सकारात्मक ऊर्जाओं का आह्वाहन करने तथा दिव्य आशीर्वाद पाने के लिए शिव अनुष्ठान की शुरुआत में पढ़ रहे हैं, जिससे समस्त पूजन से हमें कल्याणकारी फल की प्राप्ति हो। 'स्वस्ति' शब्द शुभता का प्रतीक है, जबकि 'वाचन' का अर्थ है कहना।" + "\n\n**निर्देश:**\n**तो आइए शुरू करते हैं — आप सब समस्त प्रकार के कल्याण के लिए शांत मन और आंखें बंद करके प्रार्थना करें।**",
            en: "Swasti Vachan refers to the chanting or recitation of auspicious prayers and invocations for well-being, blessings, and harmony. At the beginning of this Shiva ritual, we recite these prayers to invoke positive energies and seek divine blessings, so that we may receive beneficial and auspicious results from the entire worship. The word ‘Swasti’ symbolizes auspiciousness and welfare, while ‘Vachan’ means to speak or recite." + "\n\n**Instructions:**\n**So, let us begin — please close your eyes, calm your mind, and pray sincerely for the well-being and prosperity of all.**"
        },
        mantras: [
            {
                mantra: {
                    hi: "स्वस्ति न इन्द्रो वृद्धश्रवाः स्वस्ति नः पूषा विश्ववेदाः। स्वस्ति नस्तार्क्ष्यो अरिष्टनेमिः स्वस्ति नो बृहस्पतिर्दधातु॥\nपृषदश्वा मरुतः पृश्निमातरः शुभंयावानो विदथेषु जग्मयः। अग्निजिह्वा मनवः सूरचक्षसो विश्वे नो देवा अवसा गमन्निह॥\nभद्रं कर्णेभिः शृणुयाम देवा भद्रं पश्येमाक्षभिर्यजत्राः। स्थिरैरंगैस्तुष्टुवांसस्तनूभिर्व्यशेम देवहितं यदायुः॥\nशतमिन्नु शरदो अन्ति देवा यत्रा नश्चक्रा जरसं तनूनाम। पुत्रासो यत्र पितरो भवन्ति मा नो मध्या रीरिषतायुर्गन्तोः॥\nअदितिर्द्यौरदितिरन्तरिक्षमदितिर्माता स पिता स पुत्रः। विश्वेदेवा अदितिः पंचजना अदितिर्जातमदितिर्जनित्वम॥\nॐ द्यौः शान्तिरन्तरिक्षँ शान्ति:, पृथ्वी शान्तिरापः शान्तिरोषधयः शान्तिः। वनस्पतयः शान्तिर्विश्वे देवा: शान्तिर्ब्रह्म शान्तिः, सर्वं शान्तिः, शान्तिरेव शान्तिः, सा मा शान्तिरेधि॥\nॐ शान्तिः शान्तिः शान्तिः॥ यतो यतः समीहसे ततो नो अभयं कुरु। शं नः कुरु प्रजाभ्योऽभयं नः पशुभ्यः॥ सुशान्तिर्भवतु॥\nॐ विश्वानि देव सवितर्दुरितानि परा सुव यद् भद्रं तन्न आ सुव॥",
                    en: "Swasti na Indro Vriddhashravah, Swasti nah Pusha Vishvavedah; Swasti nastarkshyo Arishtanemih, Swasti no Brihaspatir Dadhatu.\nPrishadashva Marutah, Prishnimatarah, Shubham Yavano Vidatheshu Jagmayah; Agnijihva Manavah, Surachakshaso, Vishve no Deva Avasa Gamannih.\nBhadram Karnebhi Shrunuyam Deva, Bhadram Pashyem Akshabhir Yajatrah; Sthirair Angais Tushtuvansastanubhir Vyashema, Devahitam Yadayuh.\nShatam Innu Sharado Anti Deva, Yatra Nashchakra Jarasam Tanunam; Putraso Yatra Pitara Bhavanti, Ma no Madhya Reerishat Ayurgantoh.\nAditir Dyaur Aditir Antariksham, Aditir Mata Sa Pita Sa Putrah; Vishvedevo Aditih Panchajana, Aditir Jatam Aditir Janitvam.\nOm Dyauh Shantir Antariksam Shantih, Prithvi Shantir Apah Shantir Oshadhayah Shantih; Vanaspatayah Shantir Vishve Devah Shantih, Brahma Shantih, Sarvam Shantih, Shantireva Shantih, Sa Ma Shantiredhi.\nOm Shantih Shantih Shantih.\nYato Yatah Sameehase Tato No Abhayam Kuru. Sham Nah Kuru Prajabhyo Abhayam Nah Pashubhyah. Sushantir Bhavatu.\nOm Vishvani Deva Savitar Duritani Para Suv Yad Bhadram Tan Na Asuv."
                },
                meaning: {
                    hi: "हम स्थावर-जंगम के स्वामी, बुद्धि को सन्तोष देनेवाले रुद्रदेवता का रक्षा के निमित्त आवाहन करते हैं। वैदिक ज्ञान एवं धन की रक्षा करने वाले, पुत्र आदि के पालक, अविनाशी पुष्टि-कर्ता देवता हमारी वृद्धि और कल्याण के निमित्त हों। महान कीर्ति वाले ऐश्वर्यशाली इन्द्र हमारा कल्याण करें, वे पूषा (सूर्य) हमारा कल्याण करें। जिनकी चक्रधारा के समान गति को कोई रोक नहीं सकता, वे गरुड़देव हमारा कल्याण करें। वेदवाणी के स्वामी बृहस्पति हमारा कल्याण करें। चितकबरे घोड़ों वाले, अदिति माता से उत्पन्न, यज्ञ में जाने वाले मरुद्गण और विश्वेदेव देवता हमारे यज्ञ में आएँ। हम दृढ़ शरीर से पुत्रों सहित आपकी स्तुति करें, कानों से शुभ सुनें, नेत्रों से शुभ देखें, देवोपासना योग्य आयु प्राप्त करें। हे देवताओं! सौ वर्षों तक हमारे साथ रहें। अखण्डित पराशक्ति ही माता, पिता, पुत्र तथा सम्पूर्ण ब्रह्मांड का स्वरूप है। सब देवता, मनुष्य और उत्पन्न वस्तुएँ उसी पराशक्ति के स्वरूप हैं। सम्पूर्ण सृष्टि में शान्ति और कल्याण हो। समस्त लोक, देवता, औषधियाँ, वनस्पतियाँ सब शान्तिदायक हों। सम्पूर्ण संसार में शान्ति ही शान्ति हो।",
                    en: "We invoke Rudra, the lord of the movable and immovable, the bestower of satisfaction to the intellect, for protection. The deity who guards Vedic knowledge and wealth, protector of sons and others, the imperishable nourisher—may He be for our growth and welfare. May the mighty, glorious, and wealthy Indra bless us; may Pūṣa (the Sun), the nourisher, bless us; may Garuḍa, whose swift movement none can restrain, bless us; and may Brihaspati, lord of sacred speech, bless us. May the Maruts and Vishvedevas, the beneficent sons of Aditi, come to our sacrifice. O protectors of the worshipper, may we, with firm limbs and our children, praise you, hear auspicious words, and see auspicious things, living a life worthy of divine service. O gods, remain with us for a hundred years; let our lifespan not be cut short. The Supreme Power is mother, father, son, and pervades all as heaven and space. All gods and beings are forms of this Power. May all realms—heaven, space, earth, waters, plants, and gods—be peace-bearing. May Brahman, the Supreme Lord, give us peace; may the entire universe be filled with peace, and may that peace grow within and around us eternally."
                }
            },
            {
                mantra: {
                    hi: "श्रीमन्महागणाधिपतये नमः । लक्ष्मीनारायणाभ्यां नमः । उमा महेश्वराभ्यां नमः । वाणीहिरण्यगर्भाभ्यां नमः । शचीपुरन्दराभ्यां नमः । मातृ-पितृ-चरणकमलेभ्यो नमः । इष्टदेवताभ्यो नमः । कुलदेवताभ्यो नमः । ग्रामदेवताभ्यो नमः । वास्तुदेवताभ्यो नमः । स्थानदेवताभ्यो नमः । सर्वेभ्यो देवेभ्यो नमः । सर्वेभ्यो ब्राह्मणेभ्यो नमः । ॐ सिद्धि बुद्धिसहिताय श्रीमन्महागणाधिपतये नमः । सुमुखश्चैकदन्तश्च कपिलो गजकर्णकः । धूम्रकेतुर्गणाध्यक्षो भालचन्द्रो गजाननः ॥ द्वादशैतानि नामानि यः पठेच्छृणुयादपि । विद्यारम्भे पूजने चैव प्रवेशे निर्गमे तथा । सङ्ग्रामे संकटे चैव विघ्नस्तस्य न जायते ॥",
                    en: "Shriman Mahaganadhipataye Namah Lakshmi Narayanabhyam Namah Uma Maheshwarabhyam Namah Vani Hiranyagarbhabhyam Namah Shachi Purandarabhyam Namah Matru-Pitru Charan Kamalebhyo Namah Ishta Devatabhyo Namah Kula Devatabhyo Namah Grama Devatabhyo Namah Vastu Devatabhyo Namah Sthana Devatabhyo Namah Sarvebhyo Devebhyo Namah Sarvebhyo Brahmanebhyo Namah Om Siddhi Buddhi Sahitaya Shriman Mahaganadhipataye Namah Sumukha Chaikadantashcha Kapilo Gajakarnakah; Dhumraketur Ganadhyaksho Bhalachandro Gajananah. Dvadashaitani Namani Yah Pathecchrunuyadapi; Vidyarambhe Poojne Chaiva Praveshe Nirgame Tathaa; Sangrame Sankate Chaiva Vighnastasya Na Jayate."
                },
                meaning: {
                    hi: "जो व्यक्ति गणेश जी के इन बारह नामों का (सुमुख, एकदंत, कपिल, गजकर्णक, धूम्रकेतु, गणाध्यक्ष, भालचंद्र, गजानन आदि) स्मरण करता है — उसे विद्यारंभ, पूजा, यात्रा, युद्ध या किसी भी संकट के समय कोई विघ्न नहीं होता। गणेश जी उसके सभी कार्यों में सफलता प्रदान करते हैं।",
                    en: "Whoever remembers the twelve names of Lord Ganesha (Sumukh, Ekadanta, Kapila, Gajakarna, Dhoomraketu, Ganadhyaksha, Bhalachandra, Gajanan, etc.)— He faces no obstacles during the beginning of learning, worship, journeys, battles, or any crisis. Lord Ganesha grants success in all his endeavors."
                }
            },
            {
                mantra: {
                    hi: "शुक्लाम्बरधरं देवं शशिवर्णं चतुर्भुजम् । प्रसन्नवदनं ध्यायेत् सर्व विघ्नोपशान्तये ॥",
                    en: "Shuklambaradharam Devam, Shashivarnam Chaturbhujam; Prasannavadanam Dhyaayet, Sarva Vighnopashantaye."
                },
                meaning: {
                    hi: "श्वेत वस्त्र धारण करने वाले, चन्द्रमा के समान उज्ज्वल, चार भुजाओं वाले, और प्रसन्नमुख भगवान गणेश का ध्यान करने से सभी विघ्न और बाधाएँ शांत हो जाती हैं।",
                    en: "Meditating on Lord Ganesha, who wears white garments, is as radiant as the moon, has four arms, and a joyful face—all obstacles and hindrances are pacified and removed."
                }
            },
            {
                mantra: {
                    hi: "अभीप्सितार्थसिद्ध्यर्थं पूजितो यः सुरासुरैः । सर्वविघ्नहरस्तस्मै गणाधिपतये नमः ॥",
                    en: "Abhiptārtha Siddhyartham Poojito Yah Surāsuraih; Sarva Vighnaharatasmai Ganadhipataye Namah."
                },
                meaning: {
                    hi: "देवता और असुर दोनों ही जिनकी पूजा अपनी इच्छाओं की सिद्धि के लिए करते हैं, ऐसे सर्वविघ्नहर गणाधिपति (गणेश जी) को मेरा नमन है।",
                    en: "Both gods and demons worship Him for the fulfillment of their desires—To such the remover of all obstacles, Lord Ganadhipati (Ganesha), I bow in reverence."
                }
            },
            {
                mantra: {
                    hi: "सर्वमङ्गलमाङ्गल्ये ! शिवे ! सर्वार्थसाधिके । शरण्ये त्र्यम्बके ! गौरि ! नारायणि ! नमोऽस्तु ते ॥",
                    en: "Sarva Mangala Mangalye, Shive, Sarvartha Sadhike; Sharanye Tryambake, Gauri, Narayani, Namo’stu Te."
                },
                meaning: {
                    hi: "हे माँ पार्वती! आप सब मंगलों में श्रेष्ठ, कल्याणदायिनी, सभी कामनाओं को पूर्ण करने वाली, त्र्यम्बका (तीन नेत्रों वाली) और सबकी शरण देने वाली देवी हैं — आपको मेरा नमस्कार है।",
                    en: "O Mother Parvati! You are the best among all auspicious beings, the giver of welfare, fulfiller of all desires, the Triambaka (three-eyed) and the refuge of all — I bow to you."
                }
            },
            {
                mantra: {
                    hi: "सर्वदा सर्वकार्येषु नास्ति तेषाममङ्गलम् । येषां हृदिस्थो भगवान् मङ्गलाय तनो हरिः॥",
                    en: "Sarvada Sarvakaryeshu Nasti Teshām Amangalam; Yesham Hridaystho Bhagavan Mangalaaya Tano Harih."
                },
                meaning: {
                    hi: "जिनके हृदय में सदैव भगवान हरि (विष्णु) निवास करते हैं, उनके सभी कार्यों में कभी अमंगल नहीं होता — उनके लिए सब कुछ शुभ ही होता है।",
                    en: "In whose hearts Lord Hari (Vishnu) always dwells, all their deeds are never inauspicious — for them, everything is always auspicious."
                }
            },
            {
                mantra: {
                    hi: "तदेव लग्नं सुदिनं तदेव ताराबलं चन्द्रबलं तदेव । विद्याबलं देवबलं तदेव लक्ष्मीपते तेऽङ्घ्रियुगं स्मरामि ॥",
                    en: "Tadev Lagnam Sudinam, Tadev Tarabalam Chandrabalam Tadev; Vidya Balam Dev Balam Tadev, Lakshmipate Te’nghriyugam Smarami."
                },
                meaning: {
                    hi: "हे लक्ष्मीपति विष्णु! मैं आपके पवित्र चरणों का स्मरण करता हूँ — वही क्षण मेरे लिए शुभ लग्न है, वही दिन उत्तम दिन है, वही तारक और चंद्रबल है, वही विद्या और देवबल है — जब मैं आपका स्मरण करता हूँ।",
                    en: "O Lakshmipati Vishnu! I remember Your sacred feet— that very moment is an auspicious occasion for me, that very day is the best day, that is the star and the strength of the moon, that is the knowledge and divine power—whenever I remember You."
                }
            },
            {
                mantra: {
                    hi: "लाभस्तेषां जयस्तेषां कुतस्तेषां पराजयः ।येषामिन्दीवरश्यामो हृदयस्थो जनार्दनः ॥",
                    en: "Labhasteṣām Jayasteṣām Kutasteṣām Parājayah; Yeṣām Indīvarashyāmo Hridayastho Janārdanah."
                },
                meaning: {
                    hi: " जिनके हृदय में नीलकमल के समान श्यामवर्ण भगवान विष्णु (जनार्दन) विराजमान हैं, उनका सदा लाभ और जय ही जय होता है, उन्हें कभी पराजय नहीं मिलती।",
                    en: " In whose heart resides Lord Vishnu (Janardana) with a dark complexion like the blue lotus, they always attain gain and victory; defeat never touches them."
                }
            },
            {
                mantra: {
                    hi: "यत्र योगेश्वरः कृष्णो यत्र पार्थो धनुर्धरः । तत्र श्रीर्विजयो भूतिर्धुवा नीतिर्मतिर्मम ॥",
                    en: "Yatra Yogeshwarah Krishno, Yatra Partho Dhanur Dharah; Tatra Shrir Vijayo Bhutir Dhruva Neetir Matir Mama."
                },
                meaning: {
                    hi: " जहाँ भगवान श्रीकृष्ण (योगेश्वर) और अर्जुन (धनुर्धर) उपस्थित हैं, वहाँ सदा श्री (समृद्धि), विजय, और नीति का वास रहता है — यह मेरा दृढ़ मत है।",
                    en: " Where Lord Shri Krishna (the Lord of Yoga) and Arjuna (the archer) are present, there always dwell prosperity, victory, and righteousness — this is my firm belief."
                }
            },
            {
                mantra: {
                    hi: "अनन्याश्चिन्तयन्तो मां ये जनाः पर्युपासते । तेषां नित्याभियुक्तानां योगक्षेमं वहाम्यहम् ॥",
                    en: "Ananyāśchintayanto māṁ ye janāḥ paryupāsate. Teṣāṁ nityābhiyuktānāṁ yogakṣemaṁ vahāmyaham."
                },
                meaning: {
                    hi: "जो भक्त मुझमें पूर्ण रूप से मन लगाकर मेरी उपासना करते हैं, उनके योग (अप्राप्त वस्तु की प्राप्ति) और क्षेम (प्राप्त वस्तु की रक्षा) का भार मैं स्वयं उठाता हूँ।",
                    en: "Those devotees who worship Me with full devotion, I Myself take the responsibility for their yoga (attainment of desired goals) and kshema (protection of what is attained)."
                }
            },
            {
                mantra: {
                    hi: "स्मृतेः सकलकल्याणं भाजनं यत्र जायते । पुरुषं तमजं नित्यं व्रजामि शरणं हरिम् ॥",
                    en: "Smriteh Sakala Kalyanam, Bhajanam Yatra Jayate; Purusham Tamajam Nityam, Vrajami Sharanam Harim."
                },
                meaning: {
                    hi: "जिस पुरुष (हरि) के स्मरण मात्र से ही सब प्रकार के कल्याण का उदय होता है, उस अजन्मा, सनातन भगवान हरि की मैं शरण लेता हूँ।",
                    en: "I take refuge in that unborn, eternal Lord Hari, by whose mere remembrance all kinds of welfare arise."
                }
            },
            {
                mantra: {
                    hi: "सर्वेष्वारम्भकार्येषु त्रयस्त्रिभुवनेश्वराः । देवा दिशन्तु नः सिद्धिं ब्रह्मेशानजनार्दनाः ॥",
                    en: "Sarveshvarambhakaryeshu, Trayastribhuvaneshvarah; Deva Dishantu Nah Siddhim, Brahmeshān Janārdanah.॥"
                },
                meaning: {
                    hi: " हमारे सभी कार्यारम्भों में त्रिलोकी के स्वामी — ब्रह्मा, विष्णु और महेश — हमको सिद्धि (सफलता) प्रदान करें।",
                    en: " May the Lords of the three worlds — Brahma, Vishnu, and Mahesh — grant us success in all our undertakings."
                }
            },
            {
                mantra: {
                    hi: " विश्वेशं माधवं दुण्ढिं दण्डपाणिं च भैरवम् । वन्दे काशीं गुहां गङ्गां भवानीं मणिकर्णिकाम् ॥",
                    en: " Vishvesham Madhavam Dundhim Dandapanim Cha Bhairavam; Vande Kashim Guham Gangam Bhavanim Manikarnikām."
                },
                meaning: {
                    hi: "मैं विश्वेश्वर (सर्वव्यापक भगवान), माधव (विष्णु), दंडधारी भैरव को प्रणाम करता हूँ। यह भाग भगवान के विविध रूपों और उनके शक्ति स्वरूप का सम्मान करता है। काशी, गुप्तगृह (गुहा), पवित्र गंगा नदी, माता भवानी (पार्वती), मणिकर्णिका को प्रणाम करता हूँ।",
                    en: "I bow to Vishweshwar (the all-pervading Lord), Madhav (Vishnu), and Dandadhari Bhairav. This part honors the various forms of the Lord and His power manifestations. I bow to Kashi, the secret cave (Guha), the sacred Ganga river, Mother Bhavani (Parvati), and Manikarnika."
                }
            },
            {
                mantra: {
                    hi: " वक्रतुण्ड महाकाय कोटिसूर्यसमप्रभ । निर्विघ्नं कुरु मे देव सर्वकार्येषु सर्वदा ॥",
                    en: " Vakratunda Mahakaya, Kotisurya Samaprabha; Nirvighnam Kuru Me Deva, Sarva Karyeshu Sarvada."
                },
                meaning: {
                    hi: "आपका रूप महाकाय (विशाल शरीर) और कोटि-सूर्य के समान तेजस्वी है। हे देव! कृपया मेरे सभी कार्यों में सदैव कोई विघ्न न आए, सब सफल हों। संक्षेप में, यह श्लोक भगवान गणेश की महिमा का वर्णन और सभी कार्यों में विघ्न निवारण की प्रार्थना करता है।",
                    en: "Your form is gigantic (Mahakaya) and radiant like a million suns. O Lord! Kindly ensure that no obstacles ever come in my way, and may all my tasks be successful. In brief, this verse describes the glory of Lord Ganesha and prays for the removal of obstacles in all endeavors."
                }
            },
            {
                mantra: {
                    hi: " विनायकं गुरू भानु ब्रह्मं विष्णु महेश्वरान। सरस्वती प्रणम्यादौ सर्ब कामार्थ सिद्धय॥",
                    en: " Vinayakam Guru Bhanu, Brahma Vishnu Maheshwaran; Saraswati Pranamyādau, Sarva Kāmārtha Siddhaya."
                },
                meaning: {
                    hi: "मैं विनायक (गणेश), गुरु (बृहस्पति), भानु (सूर्य), ब्रह्मा, विष्णु और महेश्वर (शिव) को प्रणाम करता हूँ। और सरस्वती माता को प्रथम प्रणाम कर, मैं यह प्रार्थना करता हूँ कि मेरे सभी काम और इच्छाएँ सिद्ध हों। संक्षेप में, यह श्लोक सभी प्रमुख देवों और माता सरस्वती का सम्मान करता है और सर्वकामार्थ सिद्धि की प्रार्थना व्यक्त करता है।",
                    en: "I bow to Vinayaka (Ganesha), Guru (Brihaspati), Bhanu (Sun), Brahma, Vishnu, and Maheshwara (Shiva). And with foremost reverence to Mother Saraswati, I pray that all my tasks and desires be fulfilled. In short, this verse honors all the principal deities and Mother Saraswati, expressing a prayer for the fulfillment of all wishes."
                }
            },
        ]
    },
    {
        id: 8,
        title: {
            hi: "संकल्पम्",
            en: "Sankalpam (Resolution/Intention)"
        },
        description: {
            hi: "अब हम संकल्प करेंगे जिसमें हम यह प्रार्थना करेंगे कि हे भगवान, हम जो भी पूजन करने जा रहे हैं वह सफल हो। इस संकल्प में हम अपने अंदर विद्यमान सभी प्रकार की बुराइयों को दूर करने और भगवान शिव की विशेष कृपा प्राप्त करने का निश्चय करते हैं।" + "\n\n**निर्देश:**\n**कृपया ध्यानपूर्वक सुनें और अपने हाथ में जल लेकर संकल्प करें।**",
            en: "Now, we shall take the Sankalpa (sacred vow or intention), in which we pray to the Almighty that the worship we are about to perform may be successful and complete. Through this Sankalpa, we also resolve to overcome all negativities and impurities within ourselves and seek the special grace and blessings of Lord Shiva." + "\n\n**Instructions:**\n**Please listen attentively and with devotion as we make this sacred resolve together.**"
        },
        mantras: [
            {
                mantra: {
                    hi: "ॐ विष्णुर्विष्णुर्विष्णुः। श्रीमद्भगवतो महापुरुषस्य विष्णोराज्ञया प्रवर्तमानस्य अद्य ब्रह्मणोऽह्नि द्वितीये परार्धे श्रीश्वेतवाराहकल्पे वैवस्वतमन्वन्तरे अष्टाविशतितमे युगे कलियुगे कलिप्रथमचणे भूर्लोके भारतवर्षे जम्बूद्वीपे भरतखण्डे आर्यावर्तान्तर्गत ब्रह्मावर्तस्य अमुकक्षेत्रे अमुकदेशे अमुकनाम्नि नगरे (ग्रामे वा) श्रीनर्मदायाः समीपस्थ देशे ………… (उत्तर/दक्षिण) दिग्भागे देवब्राह्मणानां सन्निधौ श्रीमन्नृपतिवीरविक्रमादित्यसमयतः ……… संख्या-परिमिते प्रवर्त्तमान संवत्सरे प्रभवादिषष्ठि-संवत्सराणां मध्ये ………… नाम संवत्सरे, ………… अयने, ………… ऋतौ, ………… मासे, ………… पक्षे, ………… तिथौ, ………… वासरे, ………… नक्षत्रे, ………… योगे, ………… करणॆ, ………… राशिस्थिते चन्द्रे, ………… राशिस्थिते श्रीसूर्ये, ………… देवगुरौ शेषेषु ग्रहेषु यथायथा राशिस्थानस्थितेषु सत्सु एवं ग्रहगुण विशेषणविशिष्टायां शुभपुण्यतिथौ ………… गोत्रोत्पन्नस्य ………… अमुकशर्मणः (वर्मणः, गुप्तस्य वा) सपरिवारस्य ममात्मनः श्रुतिस्मृतिपुराणोक्तपुण्यफलावाप्त्यर्थं मम ऐश्वर्याभिवृद्ध्यर्थं अप्राप्तलक्ष्मीप्राप्त्यर्थं प्राप्त लक्ष्म्याः चिरकाल संरक्षणार्थं सकलमनः इप्सितकामनासिद्ध्यर्थं लोके वा सभायां राजद्वारे वा सर्वत्र यशोविजयलाभादि प्राप्त्यर्थं समस्त भयव्याधिजरापीडामृत्युपरिहारद्वारा आयुरारोग्यैश्वर्याद्यभिवृद्ध्यर्थं तथा च मम जन्मराशेः सकाशाद्ये केचिद विरुद्ध चतुर्थाष्टमद्वादशस्थानस्थिताः क्रूरग्रहाः तैः सूचितं सूचयिष्यमाणञ्च यत् सर्वारिष्टं तद्विनाशद्वारा सर्वदा तृतीयैकादशस्थानस्थितवच्छुभफल प्राप्त्यर्थं पुत्रपौत्रादि संततेरविच्छिन्न वृद्ध्यर्थम् आदित्यादि नवग्रह अनुकूलता सिद्ध्यर्थं इन्द्रादि दशदिक्पाल प्रसन्नता सिद्ध्यर्थं आधिदैविकाधिभौतिकाध्यात्मिका त्रिविधतापोपशमनार्थं धर्मार्थकाममोक्ष फलावाप्त्यर्थं यथाज्ञानं यथा मिलितोपचारद्रव्यैः ………… भगवतः साम्बसदा शिवस्य पूजनं अहं करिष्ये।",
                    en: "Om Vishnur Vishnur Vishnuh. Shrimad Bhagavato Mahapurushasya Vishnorājñayā pravartamānasya adya Brahmaṇo’hnī dvitīye parārdhe, Shri Shvetavarāhakālpe, Vaivasvatamanvantare, aṣṭāviśatitame Yuge, Kaliyuge, Kaliprathamachane, Bhurloke, Bhāratavarṣe, Jambūdvīpe, Bharatakhaṇḍe, Āryāvarta antaragata Brahmāvartasya amuk kṣetre, amuk deśe, amuk nāmninagare (grāme vā), Śrī Narmadāyāḥ samīpastha deśe ………… (uttare/dakshine) digbhāge, devabrāhmaṇānām sannidhau, Śrīman Nṛpatī Vīravikramāditya samayatāḥ ……… saṅkhyā-parimite pravartamānasamvatsare, prabhavādiṣaṣṭhi-samvatsarāṇām madhye ………… nāma-samvatsare, ………… ayane, ………… ṛtau, ………… māse, ………… pakṣe, ………… tithau, ………… vā­sare, ………… nakṣatre, ………… yoge, ………… karaṇe, ………… rāśisthite candre, ………… rāśisthite Śrī Sūrye, ………… devagurau Śeṣeṣu graheṣu yathāyathā rāśisthānasthiteṣu satsu, evaṃ grahaguṇa-viśeṣaṇa-viśiṣṭāyāṃ śubha-puṇya-tithau ………… gotrottpannasya ………… amuk Sharmaṇah (Varman:, Guptasya vā), mam saparivārasya mamātmanah śrutismṛtipurāṇokta-puṇyaphalāvāptyartham, mam aiśvaryābhivṛddhyartham, aprāptalakṣmī-prāptyartham, prāpta lakṣmyāścirakāla saṃrakṣaṇārtham, sakalmanah ipsitakāmanā-sam̐siddhyartham, loke va sabhāyāṃ rājadvāre vā sarvatra yaśo-vijaylābhādi prāptyartham, samastabhayavyādhi-jarāpīḍā-mṛtyuparihāradvārā āyur-ārogya-aiśvaryādya-abhivṛddhyartham, tathā ca mam janmarāśeḥ sakāśādye kecid viruddha-caturtha-aṣṭama-dvādaśasthānasthitāḥ krūra-grahāḥ taiḥ sūcitaṃ sūcayiṣyamānaṃ ca yat sarvāriṣṭaṃ tadvināśadvārā sarvadā tṛtīya-ikādaśasthānasthitavacchubha-phalaprāptyartham, putrapautra-dī-santatēr avicchinna-vṛddhyartham, Ādityādīn navagrahānūkūlatā siddhartham, Indrādi-daśadik-pāla prasannatā-siddhartham, ādhidaivikādhi-bhautika-adhyātmikā-trividhatāpopaśamanārtham, dharma-artha-kāma-mokṣa-phalāvāptyartham, yathā jñānaṃ, yathā militopacāra-dravyaiḥ ………… Bhagavataḥ Sāmbasada Śivasya pūjanaṃ ahaṃ kariṣye."
                },
                meaning: {
                    hi: "यह संकल्प इस बात का सूचक है कि हम भगवान की अनुमति और कृपा से, सही स्थान, सही समय और सभी शुभ ग्रहस्थितियों में भगवान शिव का पूजन करने जा रहे हैं। इसमें हम अपने गोत्र और नाम के साथ अपनी सभी इष्ट कामनाओं, समस्त भय, व्याधि और मृत्यु से रक्षा, आयु, स्वास्थ्य और ऐश्वर्य की वृद्धि, पुत्र-पौत्र संतति की वृद्धि, धर्म, अर्थ, काम और मोक्ष की प्राप्ति, सभी ग्रहों की अनुकूलता, सभी देवताओं और दिक्पालों की प्रसन्नता, और तीनों प्रकार के कष्टों के उपशमन का संकल्प करते हैं।",
                    en: "This Sankalpa signifies that, with the permission and grace of the Almighty, we are about to perform the worship of Lord Shiva at the proper place and time, under auspicious planetary influences. It includes the declaration of our lineage and name, and it sets the intention for fulfilling all desires, protection from fear, illness, and death, increase in longevity, health, and prosperity, uninterrupted growth of descendants, attainment of dharma, artha, kama, and moksha, favor of all planets and gods, blessings of the directional deities, and the alleviation of all three types of suffering."
                }
            }
        ]
    },
    {
        id: 9,
        title: {
            hi: "शिवपूजनं प्रारंभ",
            en: "Beginning of Shiva Worship (Shiv Poojanam Prarambh)"
        },
        description: {
            hi: "अब हम संकल्प करने के पश्चात् शिवपूजन प्रारंभ करेंगे। भक्तों, आप भी ध्यानपूर्वक इस पूजन को देखें और यदि संभव हो तो किसी स्वच्छ स्थान पर बैठकर भगवान शिव से समस्त प्रकार के कल्याण के लिए प्रार्थना करें। पूजा के दौरान आने वाली बाधाओं को दूर करने और सभी कार्यों में सफलता के लिए गणेश जी, माता गौरी और भगवान शिव के गणों का ध्यान करेंगे।" + "\n\n**निर्देश:**\n**आप भी भक्तों शिव- पूजन को ध्यान से देखें और यदि संभव होतो किसी भी स्वच्छ से स्थान में बैठकर ये पूजन देखें और भगवान शिव से समस्त प्रकार के कल्याण के लिए प्रार्थना करें **",
            en: "After taking the Sankalpa, we shall now begin the worship of Lord Shiva (Shiv Pujan). Dear devotees, please watch the Shiva Pujan attentively, and if possible, sit in a clean and pure place while observing this ritual. Use this moment to pray to Lord Shiva for all forms of prosperity and spiritual welfare. During the worship, we shall meditate upon Lord Ganesha, Goddess Gauri, and the attendants of Lord Shiva to remove obstacles and ensure success." + "\n\n**Instructions:**\n**Dear devotees, please watch the Shiva Pujan attentively, and if possible, sit in a clean and pure place while observing this ritual. Use this opportunity to pray to Lord Shiva for all forms of well-being and prosperity.**"
        },
        mantras: [
            {
                mantra: {
                    hi: "गणानां त्वा गणपतिं हवामहे प्रियाणां त्वा प्रियपतिं हवामहे निधिनां त्वा निधिपतिं हवामहे वसो मम आह्मजानि गर्भधमात्वमजास गर्भधम्॥ देवी ध्यानम् — अम्बे अम्बिके अम्बालिके नमानयति कश्चन। ससत्स्यकश्चकः सुभद्रिकां काम्पीलवासिनीम्॥ शिवगण ध्यानम् — नंदी, भृंगी, वीरभद्र, भैरव, मणिभद्र, चंडीस, श्रृंगी, जय, विजय, शैल, गोकर्ण, घंटाकर्ण, तथा भगवान कार्तिकेय जी को नमस्कार करें।",
                    en: "Ganānāṃ Tvā Gaṇapatim Havāmahe, Priyāṇāṃ Tvā Priyapatim Havāmahe, Nidhināṃ Tvā Nidhi-patim Havāmahe, Vaso Mama Āhmajāni Garbhadhmā Tvam Ajās Garbham. Devi Dhyan: Ambe, Ambike, Ambalike, Namānayati Kashchana. Sasatsyakashchakaha Subhadrikām Kāmpilavāsinīm. Shiva Gana Dhyan: Nandi, Bhringi, Veerabhadra, Bhairava, Manibhadra, Chandi, Shringi, Jai, Vijay, Shail, Gokarna, Ghantakarna, and Lord Kartikeya—salutations to all these divine attendants."
                },
                meaning: {
                    hi: "हे परमदेव गणेशजी! समस्त गणों के अधिपति और प्रिय पदार्थों के स्वामी तथा समस्त सुख-समृद्धि के निधिपति! हम आपका आवाहन करते हैं। आप सृष्टि के उत्पत्तिकर्ता हैं, हिरण्यगर्भ को धारण करने वाले हैं और संपूर्ण सृष्टि का पालन करने वाली प्रकृति के स्वामी हैं। हे देवी अम्बा! आप अम्बिके और अंबालिके हैं, और जो भी व्यक्ति आपका पूजन करता है, वह कांपिल्य में स्थित सुभद्रा देवी की भी उपासना करता है। हम भगवान शिव के गणों — नंदी, भृंगी, वीरभद्र, भैरव, मणिभद्र, चंडी, श्रृंगी, जय, विजय, शैल, गोकर्ण और घंटाकर्ण — तथा भगवान कार्तिकेय जी को प्रणाम करते हैं।",
                    en: "O Supreme Lord Ganesha! Lord of all attendants, protector of the beloved, and master of all treasures of happiness — we invoke you with devotion. You are the creator of the universe, bearer of the golden womb (Hiranyagarbha), and the lord of all that sustains creation. O Divine Mother Ambe, you are Ambike and Ambalike; whoever worships you also worships Subhadra residing in Kampil. We bow to the attendants of Lord Shiva — Nandi, Bhringi, Veerabhadra, Bhairava, Manibhadra, Chandi, Shringi, Jai, Vijay, Shail, Gokarna, and Ghantakarna — and to Lord Kartikeya."
                }
            }
        ]
    },
    {
        id: 10,
        title: {
            hi: "ध्यानं",
            en: "Meditation (Dhyanam)"
        },
        description: {
            hi: "अब भगवान शिव का ध्यान करें — जो समस्त ब्रह्माण्ड के अधिपति, करुणामय, भयरहित और परम तेजस्वी हैं।",
            en: "Now, let us meditate upon Lord Shiva — the supreme ruler of the universe, compassionate, fearless, and infinitely radiant."
        },
        mantras: [
            {
                mantra: {
                    hi: "ध्यायेन्नित्यं महेशं रजतगिरिनिभं चारु चन्द्रावतंसम्। रत्नाकल्पोज्ज्वलांगं परशुमृगवराभीति हस्तं प्रसन्नम्॥ पद्मासीनं समन्तात्स्तुतममरगणैर्व्याघ्रकृत्तिं वसानम्। विश्वाद्यं विश्ववन्द्यं निखिलभयहरं पंचवक्त्रं त्रिनेत्रम्॥ ॥ श्री साम्बसदाशिवाय नमः । श्री सदाशिवं ध्यायामि ॥ ध्यानार्थे पुष्पं समर्पयामि ॥",
                    en: "Dhyāyennityaṃ Maheshaṃ Rajatagirinibhaṃ Chāru Chandrāvataṃsam, Ratnākalpojvalāṃgaṃ Parashumṛgavārābhīti Hastaṃ Prasannam. Padmāsīnaṃ Samantāt Stutam Amaragaṇair Vyāghrakṛttiṃ Vasānam, Viśvādyam Viśvavandyaṃ Nikhila Bhayaharaṃ Pancha Vaktṛṃ Trinetram. Śrī Sāmbasadāśivāya Namaḥ. Śrī Sadāśivaṃ Dhyāyāmi. Dhyānārthe Puṣpaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "मैं प्रतिदिन भगवान महेश (शिव) का ध्यान करता हूँ, जो रजत पर्वत के समान उज्ज्वल हैं और जिनके मस्तक पर सुंदर चंद्रमा शोभित है। उनका शरीर रत्नों के समान तेजस्वी है और वे अपने हाथों में कल्याणकारी परशु और मृग का शृंग धारण करते हैं। उनका मुख अत्यंत प्रसन्न है। वे कमलासन पर विराजमान हैं, देवगणों द्वारा पूजित हैं, बाघ की खाल को वस्त्र रूप में धारण करते हैं, वे सम्पूर्ण सृष्टि के मूल हैं, सबके द्वारा वंदित हैं, समस्त भय का नाश करने वाले हैं तथा पंचमुख और त्रिनेत्रधारी हैं।",
                    en: "I daily meditate on Lord Mahesh (Shiva), who shines like the silver mountains and bears the beautiful crescent moon upon His head. His body glows like jewels and the celestial Kalpavriksha. He holds an axe and a deer horn symbolizing power in His hands, and His face is blissful and serene. Seated upon a lotus, He is praised by divine beings all around, adorned with the skin of a tiger as His garment. He is the source and master of the universe, worshipped by all, destroyer of every fear, the five-faced and three-eyed Lord Shiva."
                }
            }
        ]
    },
    {
        id: 11,
        title: {
            hi: "आवाहन",
            en: "Invocation (Aavahan)"
        },
        description: {
            hi: "ध्यान के पश्चात् अब हम भगवान शिव का आवाहन करेंगे — हे प्रभु, हमारी पूजा में पधारें।",
            en: "After performing the meditation, we shall now invoke Lord Shiva — O Lord, please come to our worship."
        },
        mantras: [
            {
                mantra: {
                    hi: "आगच्छ देवदेवेश तेजोराशे जगत्पते। क्रियमाणमिमां पूजां गृहाण सुरसत्तम॥ आवाहितो भव॥ स्थापितो भव॥ सुप्रीतो भव॥ सुप्रसन्नो भव॥ सुमुखो भव॥ वरदो भव॥ प्रसीद प्रसीद॥",
                    en: "Āgaccha Devadeveśa Tejorāśe Jagatpate; Kriyāmaṇamimāṃ Pūjāṃ Gṛhāṇa Surasattam. Āvāhito Bhava, Sthāpito Bhava, Suprīto Bhava, Suprasanno Bhava, Sumukho Bhava, Varado Bhava, Prasīda Prasīda."
                },
                meaning: {
                    hi: "हे देवदेवेश्वर, जगत के स्वामी और तेज के भंडार! कृपा करके इस पूजन को जो हम अभी कर रहे हैं, स्वीकार करें। हे श्रेष्ठ देव! आप हमारे पूजन में पधारें, स्थापित हों, प्रसन्न हों, और कल्याणकारी मुख से आशीर्वाद प्रदान करें।",
                    en: "O Lord of Lords, master of the universe, and radiance personified! Please accept this worship that we are now performing. O supreme deity, be present in our worship, remain established here, be pleased, shine graciously upon us, and bestow blessings with Your auspicious face."
                }
            }
        ]
    },
    {
        id: 12,
        title: {
            hi: "आसन",
            en: "Seat (Aasan)"
        },
        description: {
            hi: "आवाहन के पश्चात हम भगवान शिव को सम्मान और भक्ति के साथ पुष्पों का आसन अर्पित करेंगे।",
            en: "After the invocation, we shall offer a seat of flowers (Pushpa Asan) to Lord Shiva as a gesture of reverence and devotion."
        },
        mantras: [
            {
                mantra: {
                    hi: "या ते रुद्र शिवा तनूरघोराऽपापकाशिनी। तया नस्तन्वा शन्तमया गिरिशन्ताभि चाकशीहि॥ ॥ ओं साम्ब सदाशिवाय नमः इदमासनं समर्पयामि॥",
                    en: "Yā te Rudra Śivā Tanuraghora-apāpakāśinī; Tayā nas tanvā śantamayā Giriśantābhi Cākashīhi. Om Sāmbasadāśivāya Namaḥ, Idam Āsanaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे रुद्र महादेव! आपका जो शुभ, पापहर और कल्याणकारी शरीर है, उसी शरीर से उत्पन्न यह शक्ति हमें शांति और सुख प्रदान करे। हे गिरिश (पर्वतों के स्वामी)! कृपा कर इसे स्वीकार कीजिए। यह श्लोक भगवान रुद्र के शांत, कल्याणकारी स्वरूप का ध्यान करते हुए उनसे कल्याण व संरक्षण की प्रार्थना करता है। मैं यह श्रेष्ठ और दिव्य आसन आपको अर्पित करता हूँ।",
                    en: "O Rudra, the great Lord! May Your auspicious and sin-destroying form bring us peace and well-being. O Lord of the Mountains, please accept this offering with Your gracious vision. This verse praises the peaceful and benevolent nature of Rudra and seeks His protection and blessings. I humbly offer this sacred and divine seat to You."
                }
            }
        ]
    },
    {
        id: 13,
        title: {
            hi: "पाद्य",
            en: "Washing of Feet (Paadyam)"
        },
        description: {
            hi: "आसन देने के उपरांत हम पाद्य अर्पित करेंगे। पाद्य वह जल होता है जो भगवान के चरणों में सम्मान और भक्ति भाव से चढ़ाया जाता है।",
            en: "After offering the seat, we shall now perform Paadyam, which is the ritual of offering water to the feet of the Lord as a mark of reverence and devotion."
        },
        mantras: [
            {
                mantra: {
                    hi: "यामिषुं गिरिशन्त हस्ते बिभर्यस्तवे। शिवां गिरित्र तां कुरु मा हि सीः पुरुषं जगत्॥३॥ ॥ ओं साम्ब सदाशिवाय नमः पादयोः पाद्यं समर्पयामि॥",
                    en: "Yāmiṣuṃ Giriśanta Haste Bibharyastave; Śivāṃ Giritra Tāṃ Kuru Mā Hi Siḥ Puruṣaṃ Jagat. Om Sāmb Sadāśivāya Namaḥ Pādayoḥ Pādyam Samarpayāmi."
                },
                meaning: {
                    hi: "हे गिरिश (पर्वतों के स्वामी, भगवान शिव)! आपके हाथों में जो वस्तु है, वह आपके लिए प्रिय है। उसे सदा शुभ और कल्याणकारी बनाइए, जिससे वह संसार में मंगल का कारण बने। आप जो कर्म और वस्तुएँ धारण करते हैं, वे सदा सर्वजनहितकारी हों। मैं अत्यंत श्रद्धा के साथ आपके चरणों में पाद्य अर्पित करता हूँ।",
                    en: "O Lord of the Mountains (Lord Shiva)! That which you hold in Your hands is dear to You. Make it always auspicious and beneficial, so that it may bring welfare to the world. May all deeds and things held by You be ever for the good of all beings. With deep reverence, I offer this pādyam (water for Your feet) at Your divine lotus feet."
                }
            }
        ]
    },
    {
        id: 14,
        title: {
            hi: "अर्घ्य",
            en: "Offering (Arghya)"
        },
        description: {
            hi: "पाद्य अर्पित करने के उपरांत हम भगवान शिव को अर्घ्य अर्पित करेंगे। अर्घ्य वह जल है जो भगवान के हाथों में सम्मान और भक्ति भाव से चढ़ाया जाता है।",
            en: "After offering Paadyam, we shall now offer Arghya to Lord Shiva. Arghya is the sacred water offering made to the hands of the Lord as a gesture of reverence and devotion."
        },
        mantras: [
            {
                mantra: {
                    hi: "शिवेन वचसा त्वा गिरिशा च्छा वदामसि। यथा नः सर्वमिज्जगदयक्ष्म सुमनाऽअसत्॥४॥ ॥ ओं साम्ब सदाशिवाय नमः हस्तयोः अर्घ्यं समर्पयामि॥",
                    en: "Śivena Vacasā Tvā Giriśa Cchā Vadāmasi; Yathā Naḥ Sarvamij Jagad Ayakṣma Sumanāʾ Asat. Om Sāmb Sadāśivāya Namaḥ, Hastyoh Arghyam Samarpayāmi."
                },
                meaning: {
                    hi: "हे महादेव शिव! मैं आपके वचनों और कृपा से यह प्रार्थना करता हूँ कि जैसे आप संपूर्ण जगत के लिए सदैव कल्याणकारी और शुभ हैं, वैसे ही हमारे लिए भी कल्याणकारी रहें। हे गिरिश (पर्वतों के स्वामी)! आप हमें सद्बुद्धि, शुभ विचार और शांति प्रदान करें। मैं यह अर्घ्य आपके पवित्र हाथों में श्रद्धा सहित अर्पित करता हूँ, कृपया इसे स्वीकार करें और प्रसन्न हों।",
                    en: "O great Lord Shiva! Guided by Your divine words and grace, I pray that You, who are ever auspicious and benevolent toward the entire universe, remain so toward us as well. O Lord of the Mountains, bless us with noble thoughts, peace, and righteousness. I humbly offer this sacred Arghya water at Your divine hands, please accept it with delight."
                }
            }
        ]
    },
    {
        id: 15,
        title: {
            hi: "आचमन",
            en: "Sipping Ritual (Aachman)"
        },
        description: {
            hi: "अर्घ्य अर्पित करने के बाद हम अब आचमन करेंगे। आचमन वह पवित्र जल है जो भगवान के मुख में भक्ति और सम्मान के साथ अर्पित किया जाता है, जिससे शुद्धि और कल्याण होता है।",
            en: "After offering Arghya, we shall now perform Aachman, the sacred ritual of offering water to the mouth of the Lord as a gesture of purification and reverence."
        },
        mantras: [
            {
                mantra: {
                    hi: "अध्यवोचदधिवक्ता प्रथमो दैव्यो भिषक्। अर्हीश्च सर्वाञ्जम्भयन्त्सर्वाश्च यातुधान्योऽधराचीः परासुव॥५॥ ॥ ओं साम्ब सदाशिवाय नमः मुखे आचमनीयं जलं समर्पयामि॥",
                    en: "Adhyavocadadhivaktā Prathamo Daivyo Bhishak; Arhīścha Sarvāñjambhayant Sarvāścha Yātudhānyoʾdharācīḥ Parāsuva. Om Sāmb Sadāśivāya Namaḥ Mukhe Āchamaniyam Jalam Samarpayāmi."
                },
                meaning: {
                    hi: "हे भगवान शिव! आप प्रथम, दिव्य और श्रेष्ठ वैद्य हैं। आप सभी प्राणियों को भय से मुक्त करते हैं और सभी राक्षसों, दुष्ट आत्माओं और पापियों का नाश करते हैं। आपकी महिमा से संपूर्ण जगत में सुरक्षा और कल्याण आता है। मैं यह जल गंगाजल के समान शुद्धता से अर्पित कर रहा हूँ, कृपया इसे स्वीकार करें और अपनी कृपा व प्रसन्नता प्रदान करें।",
                    en: "O Lord Shiva! You are the foremost, divine, and supreme healer. You release all beings from fear and destroy demons, evil spirits, and sinners. Through Your glory, safety and welfare prevail in the whole universe. I offer this water, prepared with purity like the holy Ganga, at Your divine mouth — please accept it and bestow Your grace and joy upon us."
                }
            }
        ]
    },
    {
        id: 16,
        title: {
            hi: "स्नान के लिए जल अर्पण",
            en: "Offering Water for Bath (Snaan ke liye Jal Arpan)"
        },
        description: {
            hi: "आचमन अर्पण के बाद हम भगवान शिव के स्नान हेतु पवित्र जल अर्पित करेंगे। यह जल भगवान के संपूर्ण शरीर पर चढ़ाया जाता है और शुद्धि एवं पवित्रता का प्रतीक है।",
            en: "After offering Achaman, we shall now offer sacred water for Lord Shiva’s divine bath. This water is poured over the entire body of the Lord, symbolizing purification and sanctity."
        },
        mantras: [
            {
                mantra: {
                    hi: "असौ यस्ताम्म्रोऽअरुणऽउत बभ्रुः सुमङ्गलः। ये चैन रुद्राऽअभितोदिक्षु श्रिताः सहस्रशोऽवैषा हेड ईमहे॥ ॥ ओं साम्ब सदाशिवाय इदं स्नानीयं जलं समर्पयामि नमः॥",
                    en: "Asauyastāmmroʾarunaʾutabbhruḥ Sumangalaḥ; Ye Chain Rudrāʾabhitodikṣuśritāḥ Sahasraśoʾvaiṣā Heḍʾeemahe. Jalam Om Sāmb Sadāśivāya Idam Snānīyam Jalam Samarpayāmi Namaḥ."
                },
                meaning: {
                    hi: "हे महादेव! यह वही ताम्रवर्ण और अरुणवर्ण वाले रुद्र हैं जिनकी भृकुटियाँ शुभ और मंगलकारी हैं। ये रुद्र सभी दिशाओं में फैले हुए हैं और इनकी संख्या हजारों में है। हम इन सहस्र रुद्रों के शुभ एवं कल्याणकारी स्वरूप की स्तुति करते हैं। हे शिव! मैंने आपके स्नान के लिए वह पवित्र जल अर्पित किया है जो गंगा, यमुना, नर्मदा, सरस्वती, तापी और रेवा जैसी पवित्र नदियों के तुल्य है। कृपया इसे स्वीकार करें।",
                    en: "O Mahadeva! These are the Rudras of coppery and reddish hue whose brows are auspicious. They are spread across all directions, numbering in thousands. We praise the benevolent and auspicious forms of these thousand Rudras. O Shiva! I have offered You sacred water equivalent to that from holy rivers Ganga, Yamuna, Narmada, Saraswati, Tapi, and Reva. Please accept it for Your divine bath."
                }
            }
        ]
    },
    {
        id: 17,
        title: {
            hi: "दुग्ध स्नान",
            en: "Milk Bath (Dugdha Snaan)"
        },
        description: {
            hi: "स्नान के बाद हम भगवान शिव को दुग्ध स्नान कराएंगे। यह दूध दिव्य कामधेनु से उत्पन्न है और समस्त जीवों के जीवन के लिए श्रेष्ठ एवं पवित्र माना जाता है।",
            en: "After the bath, we shall perform the sacred milk bath for Lord Shiva. This milk has originated from the divine Kamadhenu and is considered the best and pure for the life of all beings."
        },
        mantras: [
            {
                mantra: {
                    hi: "कामधेनुसमुद्भूतं सर्वेषां जीवनं परम्। पावनं यज्ञहेतुश्च पयः स्नानाय गृह्यताम्॥ ॥ ओं साम्ब सदाशिवाय नमः इदं पयःस्नानं समर्पयामि॥",
                    en: "Kāmadhenusamudbhūtaṃ Sarveṣāṃ Jīvanaṃ Param; Pāvanaṃ Yajñhetuśca Payaḥ Snānāya Gṛhyatām. Om Sāmbasadāśivāya Namaḥ, Idaṃ Payaḥ-Snānaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे भगवान! यह दूध दिव्य कामधेनु से उत्पन्न है। यह सभी जीवों के जीवन के लिए श्रेष्ठ, पवित्र और कल्याणकारी है, और यज्ञ के लिए भी उपयुक्त है। कृपया इसे अपने स्नान के लिए स्वीकार करें।",
                    en: "O Lord! This milk originates from the divine Kamadhenu. It is supreme, pure, and auspicious for the life of all beings, and also suitable for sacred offerings. Please accept it for Your holy bath."
                }
            }
        ]
    },
    {
        id: 18,
        title: {
            hi: "दधि स्नान",
            en: "Curd Bath (Dadhi Snaan)"
        },
        description: {
            hi: "दुग्ध स्नान के उपरांत अब भगवान शिव को दधि (दही) से स्नान कराया जाएगा। यह मधुर, शीतल और पवित्र माना गया है।",
            en: "After the milk bath, Lord Shiva is bathed with curd, which is sweet, cooling, and sacred."
        },
        mantras: [
            {
                mantra: {
                    hi: "पयसस्तु समुद्भूतं मधुराम्लं शशिप्रभम्। दध्यानीतं मया देव स्नानार्थं प्रतिगृह्यताम्॥ ॥ ओं साम्ब सदाशिवाय नमः इदं दधिस्नानं समर्पयामि॥",
                    en: "Payasastu Samudbhūtaṃ Madhurāmlaṃ Śaśiprabham; Dadhyānītaṃ Mayā Deva Snānārthaṃ Pratigṛhyatām. Om Sāmbasadāśivāya Namaḥ, Idaṃ Dadhisnānaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे भगवान! यह दही दूध से उत्पन्न, मधुर और चाँद के समान शीतल है। मैं इसे आपके पवित्र स्नान के लिए श्रद्धा सहित अर्पित करता हूँ।",
                    en: "O Lord! This curd, born from milk, is sweet and cool like the radiance of the moon. I offer it to You with devotion for Your sacred bath."
                }
            }
        ]
    },
    {
        id: 19,
        title: {
            hi: "घृत स्नान",
            en: "Ghee Bath (Ghrit Snaan)"
        },
        description: {
            hi: "दधि स्नान के उपरांत अब भगवान शिव को घृत (शुद्ध घी) से स्नान कराया जाएगा, जो संतोष और प्रसन्नता देने वाला है।",
            en: "After the curd bath, Lord Shiva is bathed with pure ghee, which bestows satisfaction and joy."
        },
        mantras: [
            {
                mantra: {
                    hi: "नवनीतसमुत्पन्नं सर्वसंतोषकारकम्। घृतं तुभ्यं प्रदास्यामि स्नानार्थं प्रतिगृह्यताम्॥ ॥ ओं साम्ब सदाशिवाय नमः इदं घृतस्नानं समर्पयामि॥",
                    en: "Navanītasamutpannaṃ Sarvasaṃtoṣakārakam; Ghṛtaṃ Tubhyaṃ Pradāsyāmi Snānārthaṃ Pratigṛhyatām. Om Sāmbasadāśivāya Namaḥ, Idaṃ Ghṛtasnānaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे भगवान! यह घृत नवनीत से उत्पन्न है और सर्वसंतोष प्रद है। कृपया इसे अपने पवित्र स्नान के लिए स्वीकार करें।",
                    en: "O Lord! This ghee, produced from fresh butter, bestows satisfaction and joy. Please accept it for Your sacred bath."
                }
            }
        ]
    },
    {
        id: 20,
        title: {
            hi: "मधु स्नान",
            en: "Honey Bath (Madhu Snaan)"
        },
        description: {
            hi: "घृत स्नान के बाद अब भगवान शिव को मधुस्नान कराया जाएगा। यह मधु पुष्पों और परागकणों से उत्पन्न होकर तेज और पुष्टिकर माना गया है।",
            en: "After the ghee bath, Lord Shiva is now bathed with honey. This honey, derived from flowers and pollen, is radiant and nourishing."
        },
        mantras: [
            {
                mantra: {
                    hi: "पुष्परेणुसमुत्पन्नं तेजः पुष्टिकरं मधु। सुस्वादु मधुरं दिव्यं स्नानार्थं प्रतिगृह्यताम्॥ ॥ ओं साम्ब सदाशिवाय नमः इदं मधुस्नानं समर्पयामि॥",
                    en: "Puṣparenusamutpannaṃ Tejaḥ Puṣṭikaraṃ Madhu; Susvādu Madhuraṃ Divyaṃ Snānārthaṃ Pratigṛhyatām. Om Sāmbasadāśivāya Namaḥ, Idaṃ Madhusnānaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे भगवान! यह मधु पुष्प और परागकणों से उत्पन्न है, जो तेज तथा पुष्टिदायक है। यह अत्यंत स्वादिष्ट, मधुर और दिव्य है। कृपया इसे अपने स्नान के लिए स्वीकार करें।",
                    en: "O Lord! This honey, produced from flowers and pollen, is radiant and nourishing. It is sweet, delightful, and divine. Please accept it for Your sacred bath."
                }
            }
        ]
    },
    {
        id: 21,
        title: {
            hi: "शर्करा स्नान",
            en: "Sugar Bath (Sharkara Snaan)"
        },
        description: {
            hi: "मधु स्नान के पश्चात अब भगवान शिव को शर्करा (गुड़/चीनी) से स्नान कराया जाएगा, जो शक्ति और शुभता प्रदान करती है।",
            en: "After the honey bath, Lord Shiva is now bathed with sugar (jaggery), which bestows strength and auspiciousness."
        },
        mantras: [
            {
                mantra: {
                    hi: "इक्षुसारसमुद्भूतां शर्करां पुष्टिदां शुभाम्। मलापहारिकां दिव्यां स्नानार्थं प्रतिगृह्यताम्॥ ॥ ओं साम्ब सदाशिवाय नमः इदं शर्करास्नानं समर्पयामि॥",
                    en: "Ikṣusārasamudbhūtāṃ Śarkarāṃ Puṣṭidāṃ Śubhām; Malāpahārikāṃ Divyāṃ Snānārthaṃ Pratigṛhyatām. Om Sāmbasadāśivāya Namaḥ, Idaṃ Śarkarāsnānaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे भगवान! यह शर्करा, इक्षु (गन्ने) से उत्पन्न, शक्ति और शुभता देने वाली है। यह मल निवारक और दिव्य है। कृपया इसे अपने स्नान के लिए स्वीकार करें।",
                    en: "O Lord! This sugar, produced from sugarcane, grants strength and auspiciousness. It is purifying and divine. Please accept it for Your sacred bath."
                }
            }
        ]
    },
    {
        id: 22,
        title: {
            hi: "पञ्चामृत स्नान (संपूर्ण)",
            en: "Complete Panchamrit Bath (Sampoorna)"
        },
        description: {
            hi: "संपूर्ण पञ्चामृत स्नान में दूध, दही, घृत, मधु और शर्करा सम्मिलित होते हैं। यह भगवान शिव को अर्पित किया जाता है ताकि उनके स्नान द्वारा कृपा और प्रसन्नता प्राप्त हो।",
            en: "The complete Panchamrit bath consists of milk, curd, ghee, honey, and sugar. It is offered to Lord Shiva so that, through His sacred bath, we may receive His grace and joy."
        },
        mantras: [
            {
                mantra: {
                    hi: "पयो दधि घृतं चैव मधु च शर्करान्वितं। पञ्चामृतं मयानीतं स्नानार्थं प्रतिगृह्यताम्॥ ॥ ओं साम्ब सदाशिवाय नमः इदं पञ्चामृत स्नानं समर्पयामि॥",
                    en: "Payo Dadhi Ghritam Chaiva Madhu Cha Sharkarānvitam; Panchamritam Mayānītam Snānārtham Pratigṛhyatām. Om Sāmb Sadāśivāya Namaḥ, Idam Panchamrita Snānaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे भगवान! यह पञ्चामृत — दूध, दही, घृत, मधु और शर्करा — आपके स्नान के लिए अर्पित किया जाता है। कृपया इसे स्वीकार करें और अपने स्नान द्वारा कृपा और प्रसन्नता प्रदान करें।",
                    en: "O Lord! This Panchamrit — milk, curd, ghee, honey, and sugar — is offered to You for Your holy bath. Please accept it and, through Your bath, bestow upon us Your grace and joy."
                }
            }
        ]
    },
    {
        id: 23,
        title: {
            hi: "महारूद्राभिषेकं",
            en: "Maharudra Abhishekam"
        },
        description: {
            hi: "अब पंचामृत स्नान के पश्चात हम भगवान शिव का रुद्रसूक्त के 16 मंत्रों द्वारा महारूद्राभिषेक करेंगे।" + "\n\n**निर्देश:**\n**कृपया आप श्रद्धापूर्वक रुद्राभिषेक देखें और भगवान शिव से समस्त कल्याण, सुख और समृद्धि की प्रार्थना करें।**",
            en: "After the Panchamrit Abhishek, we shall now perform the Maha Rudrabhishek of Lord Shiva using the 16 mantras of the Rudra Sukta." + "\n\n**Instructions:**\nPlease watch this sacred ritual with devotion and pray to Lord Shiva for universal welfare, happiness, and prosperity."
        },
        mantras: [
            {
                mantra: {
                    hi: "ॐ नमस्ते रुद्र मन्यव उतो त इषवे नमः। बाहुभ्यामुत ते नमः॥१॥",
                    en: "Om Namaste Rudra Manyava, Uto Ta Ishave Namah; Baahubhyaam Uta Te Namah."
                },
                meaning: {
                    hi: "हे रुद्रदेव! आपके क्रोध को नमस्कार है, आपके बाणों को नमस्कार है और आपकी दोनों भुजाओं को नमस्कार है। आपके शस्त्र और शक्ति हमें सदैव कल्याण प्रदान करें।",
                    en: "O Lord Rudra! Salutations to Your wrath, salutations to Your arrows, and salutations to both of Your mighty arms. May Your power and weapons always bring welfare and protection to us."
                }
            },
            {
                mantra: {
                    hi: "या ते रुद्र शिवा तनूरघोराऽपापकाशिनी। तया नस्तन्वा शन्तमया गिरिशन्ताभि चाकशीहि॥२॥",
                    en: "Yaa Te Rudra Shivaa Tanur-Aghoraa-'Apaapakaashinii, Tayaa Nas-Tanvaa Shantamayaa Girishantaabhichaakashihi॥2॥"
                },
                meaning: {
                    hi: "हे रुद्र महादेव! जो आपका शरीर शुभ और पापहर (अपाप नाशक) है, उसी शरीर से उत्पन्न यह शक्ति हमारे लिए शांति और कल्याण लाए, हे गिरिश (पर्वतों के स्वामी)! कृपया इसे दृष्टि और कृपा से स्वीकार करें। संक्षेप में, यह श्लोक भगवान रुद्र/शिव के पापहर, शुभ और शांति देने वाले स्वरूप का वर्णन करता है और उनसे कल्याण और संरक्षण की प्रार्थना करता है।",
                    en: "O Rudra Mahadeva! May that auspicious and sin-destroying form of Yours — from which divine power arises — bring us peace and well-being. O Girisha (Lord of the mountains), please look upon it with grace and acceptance."
                }
            },
            {
                mantra: {
                    hi: "यामिषुं गिरिशन्त हस्ते बिभर्यस्तवे। शिवां गिरित्र तां कुरु मा हि ᳬ सी: पुरुषं जगत्॥३॥",
                    en: "Yaam Ishum Girishanta Haste Bibharyastave, Shivaam Giritra Taam Kuru Maa Hiṁsiiḥ Purusham Jagat॥3॥"
                },
                meaning: {
                    hi: "हे गिरिश (गिरि के स्वामी, भगवान शिव)! आपने जिस वस्तु को अपने हाथ में धारण किया है, वह आपके लिए प्रिय है। उस वस्तु को सदा शुभ और कल्याणकारी बनाइए, ताकि वह जगत में मंगल का कारण बने। हे प्रभु! आपके द्वारा धारण किए गए कर्म और वस्तुएँ सदा सर्वजनहितकारी हों।संक्षेप में, यह श्लोक भगवान शिव की शक्ति और उनके हाथों में धारण वस्तुओं की शुभता और कल्याणकारी प्रभाव का वर्णन करता है।",
                    en: "O Girisha (Lord of the mountains, Shiva)! That which You hold in Your hand is dear to You. May that object always remain auspicious and benevolent, so that it becomes a source of welfare for the world. O Lord! May the actions You perform and the objects You hold always be for the benefit of all beings."
                }
            },
            {
                mantra: {
                    hi: "शिवेनव्वचसात्त्वागिरिशाच्छाव्वदामसि। यथा नः सर्व्वमिज्जगदयक्ष्म ᳪ सुमनाऽअसत्॥४॥",
                    en: "Shivena Vachasa Tva Girisha Achchha Vadaamasi, Yathaa Nah Sarvam Ijjagat Ayakshmam Sumanaa Asat॥4॥"
                },
                meaning: {
                    hi: "हे महादेव शिव! मैं आपके शब्दों और वचन से निर्देशित होकर यह कहता हूँ। जैसे आप सर्व जगत में सदा शुभ, मंगलकारी और कल्याणकारी हैं, वैसे ही आप हमारे लिए भी सदा प्रिय, शुभ और सुरक्षित रहें। संक्षेप में, यह श्लोक भगवान शिव की शुभता, सर्वव्यापकता और कल्याणकारी स्वरूप का वर्णन करता है और उनके प्रति भक्त का सम्मान व्यक्त करता है।",
                    en: "O Mahadeva Shiva! Guided by Your words and teachings, I say this: Just as You are always auspicious, benevolent, and a source of welfare for the entire universe, in the same way, may You always remain beloved, protective, and a bringer of goodness for us as well."
                }
            },
            {
                mantra: {
                    hi: "अध्यवोचदधिवक्ता प्रथमो दैव्यो भिषक्।अर्हीश्च सर्वाञ्जम्भयन्त्सर्वाश्च यातुधान्योऽधराचीः परासुव॥५॥",
                    en: "adhyavochadadhivakta prathamo daivyo bhishak.arheeshch sarvanjambhayantsarvasch yatudhanyo̕dharachih parasuva.5."
                },
                meaning: {
                    hi: "हे भगवान शिव! आप प्रथम, दिव्य और श्रेष्ठ चिकित्सक (भिषक्) हैं। आप ही ऐसे हैं जो सभी प्राणियों को भय से मुक्त करते हैं। आप ही सभी राक्षसों, दुष्टात्माओं और पापियों पर विजय पाने वाले हैं। आपकी महिमा से संपूर्ण जगत में सुरक्षा और कल्याण आता है। संक्षेप में, यह श्लोक भगवान शिव को सर्वशक्तिमान रक्षक, भय नाशक और सभी के कल्याणकारी के रूप में स्तुति करता है।",
                    en: "O Lord Shiva! You are the first, the divine, and the supreme healer (Bhishak). You alone free all living beings from fear. You are the conqueror of all demons, evil spirits, and sinners. Through Your divine glory, protection and welfare spread throughout the entire universe."
                }
            },
            {
                mantra: {
                    hi: "असौयस्ताम्म्रोऽअरुणऽउतबब्भ्रुः सुमङ्गलः। ये चैन ᳪ रुद्द्राऽअभितोदिक्षुश्रिताः सहस्रशोऽवैषा ᳪ हेडऽईमहे॥६॥",
                    en: "Asau Yas Taamro Aruṇa Uta Babhruḥ Sumaṅgalaḥ, Ye Cha Enam Rudrāḥ Abhito Dikṣu Shritāḥ, Sahasraśo Vaiṣhāṁ Heḍa īmahe॥6॥"
                },
                meaning: {
                    hi: "हे महादेव! यह वही ताम्रवर्ण (ताम्राभ) और अरुण (लालिमा वाले) रुद्र हैं, जिनकी भृकुटियाँ (भौहें) शुभ और मंगलकारी हैं। ये रुद्र सर्व दिशाओं में फैले हुए हैं, और उनकी संख्या हजारों में है। हम इन सहस्र रुद्रों की महिमा और शक्ति की स्तुति करते हैं। संक्षेप में, यह श्लोक भगवान रुद्र के हजारों रूपों और उनके शुभ, मंगलकारी प्रभाव का वर्णन करता है।",
                    en: "O Lord Shiva! You are the first, the divine, and the supreme healer (Bhishak). You alone free all living beings from fear. You are the conqueror of all demons, evil spirits, and sinners. Through Your divine glory, protection and welfare spread throughout the entire universe."
                }
            },
            {
                mantra: {
                    hi: "असौ योऽवसर्प्पति नीलग्ग्रीवो व्विलोहितः। उतैङ्गोपाऽअदृश्श्रन्नदृश्श्रन्नुदहार्य्यः सदृष्टो मृडयातिनः॥७॥",
                    en: "Asau Yo’vasarpati Neelagreevo Vilohitah, Utainam Gopaa Adrushann Adrushannudaharyah, Sadrishto Mridayaati Nah॥7॥"
                },
                meaning: {
                    hi: "हे महादेव नीलग्रीव (नील ग्रीवा वाले), लालिमा धारी और शक्तिशाली! आप वही हैं, जो अपने ऊपर से (आकाश) और नदियों पर वास करते हुए जगत में दिखाई देते हैं। आपके अद्भुत रूप और क्रियाएँ सतत दृष्टिगोचर और अद्भुत हैं। हे प्रभु! आपकी यह दिव्यता और महिमा हमें सदा आकर्षित और मोहित करती रहे। संक्षेप में, यह श्लोक नीलग्रीव महादेव की दिव्यता, उनके अद्भुत रूप और जगत में उनकी प्रभावशीलता की स्तुति करता है।",
                    en: "O Mahadeva Neelagriva (the blue-throated one), radiant and mighty! You are the One who dwells above in the heavens and upon the rivers, appearing visibly throughout the world. Your wondrous form and divine actions are constantly manifest and awe-inspiring. O Lord, may this divine glory and magnificence of Yours continue to enchant and captivate us forever."
                }
            },
            {
                mantra: {
                    hi: "नमोऽस्तु नीलग्ग्रीवाय सहस्राक्षाय मीढुषे।अथो ये अस्य सत्वानोऽहं तेभ्योऽकरं नमः॥८॥",
                    en: "Namo’stu Neelagreevaaya Sahasraakshaaya Meedhushe, Athō Ye Asya Satvaano’ham Tebhyo’karam Namah॥8॥"
                },
                meaning: {
                    hi: "हे महादेव नीलग्रीव (नील ग्रीवा वाले), सहस्राक्ष (हजारों नेत्रों वाले) और शक्तिशाली धनुषधारी! मैं उन सर्वश्रेष्ठ सत्संगियों और भक्तों को प्रणाम करता हूँ, जिन्होंने आपकी महिमा का सम्मान किया।हे प्रभु! आपकी कृपा से उनकी रक्षा और कल्याण हो। संक्षेप में, यह श्लोक भगवान शिव (नीलग्रीव, सहस्राक्ष) के सम्मान और उनके भक्तों के कल्याण की प्रार्थना करता है।",
                    en: "O Mahadev Neelagriva (the Blue-Throated One), Sahasraksha (the Thousand-Eyed), and powerful wielder of the bow! I bow to those supreme devotees and saints who have honored Your glory. O Lord! May Your grace protect them and grant them welfare. In summary, this verse honors Lord Shiva (Neelagriva, Sahasraksha) and prays for the protection and well-being of His devotees."
                }
            },
            {
                mantra: {
                    hi: "प्रमुञ्चधन्न्वनस्त्वमुभयोरार्त्क्ज्याम्। याश्च तेहस्तऽइषवः पराता भगवोव्वप॥९॥",
                    en: "Pramuncha Dhanvanas Tvam Ubhayor Aartni Jyām, Yāścha Te Hasta Iṣavah Parā Tā Bhagavo Vapa॥9॥"
                },
                meaning: {
                    hi: "हे भगवन् महादेव! आप ही धनुष और बाण को छोड़ने वाले, दोनों हाथों से शक्तिशाली बनाते हैं। आपके हाथों से छोड़े गए बाण और प्रभाव पूरे जगत में फैलते हैं। हे प्रभु! यह शक्ति हमारे लिए कल्याणकारी और रक्षक बनी रहे। संक्षेप में, यह श्लोक भगवान शिव के धनुष और बाण की शक्ति, उनके हाथों से फैलने वाली प्रभावशीलता और सुरक्षा का वर्णन करता है।",
                    en: "O Bhagavan Mahadev! You are the one who wields the bow and releases the arrows, empowering them with both Your hands. The arrows and their effects released from Your hands spread throughout the entire universe. O Lord! May this power remain beneficial and protective for us. In summary, this verse describes the power of Lord Shiva’s bow and arrows, their widespread influence emanating from His hands, and their protective nature."
                }
            },
            {
                mantra: {
                    hi: "व्विज्ज्यन्धनुः कपर्द्दिनो व्विशल्ल्यो बाणवाँ२ ऽउत। अनेशन्नस्ययाऽइषव ऽआभुरस्यनिषङ्गधिः॥१०॥",
                    en: "Vijyaṃ Dhanuh Kapardino Vishallyo Bāṇavāṃ Uta, Aneshannasya Shayā Iṣava Ābhurasya Nishaṅgadhiḥ॥10॥"
                },
                meaning: {
                    hi: "हे भगवान शिव! आपके धनुष और बाण अत्यंत शक्तिशाली और निर्दोष हैं। जो भी बाण आपने छोड़ा, वह सर्वशक्तिमान और अजेय है। हे प्रभु! इस धनुष और बाण की शक्ति हमारे लिए सुरक्षा और कल्याण का स्रोत बनी रहे। संक्षेप में, यह श्लोक भगवान शिव के धनुष और बाण की शक्ति, उनकी अजेयता और रक्षा देने वाले स्वरूप की स्तुति करता है।",
                    en: "O Lord Shiva! Your bow and arrows are immensely powerful and flawless. Whichever arrow You release is all-powerful and invincible. O Lord! May the power of this bow and arrow remain a source of protection and welfare for us. In summary, this verse praises the power, invincibility, and protective nature of Lord Shiva’s bow and arrows."
                }
            },
            {
                mantra: {
                    hi: "याते हेतिर्म्मीढुष्ट्टमहस्ते बभूव ते धनुः। तयास्म्मान्निवश्श्वतस्त्वमयक्ष्म्मया परिभुज॥११॥",
                    en: "Yaate Hetirm Meedhusht Tamahaste Babhuva Te Dhanuḥ, Tayāsmān Nivasas Tvam Ayakshmaya Paribhuj॥11॥"
                },
                meaning: {
                    hi: "हे महादेव! आपके धनुष की शक्ति और प्रभाव से जो भी शक्तिशाली और कुपित अस्त्र है, वह उत्पन्न हुआ। हे प्रभु! आप अपने शक्तिशाली धनुष से हमें सर्वत्र वश में रखें, हमें हर प्रकार के कष्ट और संकट से रक्षा प्रदान करें। संक्षेप में, यह श्लोक भगवान शिव के धनुष की शक्ति और उससे प्राप्त सुरक्षा की प्रार्थना करता है।",
                    en: "O Mahadev! From the power and influence of Your bow, all powerful and fierce weapons have originated. O Lord! With Your mighty bow, keep us under control everywhere and protect us from all kinds of hardships and dangers. In summary, this verse prays for the protection and security granted by the power of Lord Shiva’s bow."
                }
            },
            {
                mantra: {
                    hi: "परि ते धन्न्वनोहेतिरस्म्मान्न्व्वृणक्तु व्विश्श्वतः। अथोयऽइषुधिस्तवारेऽअस्म्मन्निधेहितम्॥१२॥",
                    en: "Pari te dhannvanohetir asmmān vṛṇaktu viśvataḥ। Athoyaiṣudhistvāre’ asmmannidhehitam॥12॥"
                },
                meaning: {
                    hi: "हे धन्वान (शिव महादेव)! आप हमारे चारों ओर रक्षा और सुरक्षा की प्रेरणा देने वाले हैं। हे प्रभु! आपका धनुष हमारे लिए सर्वत्र कल्याण और सुरक्षा का साधन बने। जो आपके धनुष से उत्पन्न शक्ति है, वह हमारे लिए हितकारी और रक्षा देने वाली हो। संक्षेप में, यह श्लोक भगवान शिव के धनुष और उसकी शक्ति से सुरक्षा और कल्याण की प्रार्थना करता है।",
                    en: "O Dhanvan (Shiva Mahadev)! You inspire protection and security all around us. O Lord! May Your bow become the means of welfare and safety for us everywhere. May the power emanating from Your bow be beneficial and protective for us. In summary, this verse prays for protection and welfare through Lord Shiva’s bow and its power."
                }
            },
            {
                mantra: {
                    hi: "अवतत्त्य धनुष्ट्व ᳬ सहस्राक्षशतेषुधे। निशीर्य्यशल्ल्यानाम्मुखा शिवोनः सुमना भव॥१३॥",
                    en: "Avatattvya dhanuṣṭva sahasrākṣaśateṣudhe। Niśīryya śallyānāmmukhā śivonaḥ sumanā bhava॥13॥"
                },
                meaning: {
                    hi: "हे शिव महादेव! आपने धनुष धारण किया, और वह धनुष सहस्राक्ष (हजारों नेत्रों) जैसा बलशाली और तेजस्वी है। हे शिव! आपके तीर (शल) के मुख से सदा शुभता और कल्याण की ऊर्जा प्रवाहित होती रहे। हे सुमना (मनोहर और प्रिय भगवान)! आपकी कृपा और शक्ति हम पर बनी रहे। संक्षेप में, यह श्लोक भगवान शिव के धनुष और शस्त्रों की महिमा, उनकी शक्ति और शुभ प्रभाव की स्तुति करता है।",
                    en: "O Shiva Mahadev! You have wielded the bow, and that bow is as powerful and radiant as Sahasraksha (the thousand-eyed one). O Shiva! May the energy of auspiciousness and welfare always flow from the mouths of Your arrows (shulas). O Sumana (the charming and beloved Lord)! May Your grace and power remain upon us. In summary, this verse praises Lord Shiva’s bow and weapons, celebrating His strength and their auspicious influence."
                }
            },
            {
                mantra: {
                    hi: "नमस्तऽआयुधायानातताय धृष्णवे। उभाब्भ्यामुतते नमो बाहुभ्यां तव धन्वने॥१४॥",
                    en: "Namast’āyudhāyānātatāya dhṛṣṇave। Ubhābhyāmutate namo bāhubhyāṃ tava dhanvane॥14॥"
                },
                meaning: {
                    hi: "हे भगवान रुद्र (शिव)! हे आयुधों के स्वामी और श्रेष्ठ शूरवीर! मैं आपको नमस्कार करता हूँ। मैं आपके दोनों हाथों और आपके धनुष को प्रणाम करता हूँ। हे महादेव! आपकी शक्ति, शौर्य और आयुधों की महिमा के सामने मैं श्रद्धा सहित नमन करता हूँ। संक्षेप में, यह श्लोक भगवान शिव / रुद्र की युद्ध, शक्ति और आयुधों में श्रेष्ठता की महिमा करता है और उनकी भक्ति में अर्पण प्रकट करता है।",
                    en: "O Lord Rudra (Shiva)! O master of weapons and supreme warrior! I bow to You. I salute Your two hands and Your bow. O Mahadeva! I offer my respectful homage to Your power, valor, and the glory of Your weapons. In summary, this verse glorifies Lord Shiva/Rudra’s excellence in battle, strength, and weapons, and expresses devotion towards Him."
                }
            },
            {
                mantra: {
                    hi: "मानोमहान्तमुतमानोऽअर्ब्भकम्मानऽउक्षन्तमुतमानऽउक्षितम्। मानोव्वधीः पितरम्मोतमातरम्मानः प्प्रियास्तन्न्वो रुद्द्ररीरिषः॥१५॥",
                    en: "Mānomahāntamutamāno’rbbhakammān’ukṣantamutamān’ukṣitam। Mānovvadhiḥ pitarammotamātaram mānaḥ priyāstannvo ruddrarīriṣaḥ॥15॥"
                },
                meaning: {
                    hi: "हे रुद्र (महादेव)! आप ही हमारे मन के महान और श्रेष्ठ अधिकारी हैं। आप हमारे कर्मों, इच्छाओं और आचरण के सर्वोच्च निरीक्षक और संरक्षक हैं। आप ही हमारे पूर्वजों (पितरों) और माताओं में प्रतिष्ठित हैं। हे रुद्र! आप हमारे प्रिय हैं, हमारी प्रार्थनाओं और अर्पणों को स्वीकार करें। संक्षेप में, यह श्लोक रुद्र की महिमा और उनके सर्वज्ञ, सर्वशक्तिमान होने का सम्मान, साथ ही पूर्वजों और माता-पिता से संबंधित पूजनीयता की प्रार्थना करता है।",
                    en: "O Rudra (Mahadev)! You alone are the great and supreme master of our mind. You are the highest observer and protector of our deeds, desires, and conduct. You are revered among our ancestors (pitrs) and mothers. O Rudra! You are dear to us; please accept our prayers and offerings. In summary, this verse praises Rudra’s greatness and his omniscience and omnipotence, while also honoring the reverence for ancestors and parents."
                }
            },
            {
                mantra: {
                    hi: "मानस्तोकेतनये मानऽआयुषिमानो गोषुमानोऽअश्वेषुरीरिषः। मानोव्वीरान्न्रुद्द्रभामिनोव्वधीर्हविष्म्मन्तः सदमित्त्वा हवामहे॥१६॥",
                    en: "Mānastoketanaye mān’āyuṣimāno goṣumāno’aśveṣurīriṣaḥ। Mānovvīrānnruddra bhāminovvadhīr havishmmantaḥ sadamittvā havāmahe॥16॥"
                },
                meaning: {
                    hi: "हे तोकेतन (मन के ध्वजधारी)! आप ही हमारे मन, आयु, और शरीर का आदर और अधिकारी हैं। आप गौ, अश्व और वीर योद्धाओं में प्रतिष्ठित हैं, और सभी देवों के समान सर्वोच्च हैं। हम आपकी सद्भावना और मित्रवत दृष्टि के साथ यह हवन (अर्पित अग्नि) कर रहे हैं। हे रुद्रभामिनी (शिव की तेजस्वी शक्ति)! हमारी इस प्रार्थना और अर्पण को स्वीकार करें। संक्षेप में, यह श्लोक मन, आयु, शक्ति और वीरता के प्रतीक रुद्र की महिमा और हवन में उनकी कृपा प्राप्ति की प्रार्थना करता है।",
                    en: "O Toketan (Bearer of the mind’s banner)! You alone are the honor and master of our mind, life, and body. You are esteemed among cows, horses, and brave warriors, and are supreme like all the gods. With your goodwill and friendly gaze, we perform this Havan (offered fire). O Rudrabhāminī (the radiant power of Shiva)! Please accept our prayer and offering. In summary, this verse glorifies Rudra as the symbol of mind, life, strength, and valor, and seeks his grace through the Havan ritual."
                }
            },
        ]
    },
    {
        id: 24,
        title: {
            hi: "गंधोदक स्नान",
            en: "Fragrant Water Bath (Gandhodaka Snan)"
        },
        description: {
            hi: "रूद्रसूक्त से महाभिषेक के पश्चात् अब हम भगवान शिव का गंधोदक स्नान कराएंगे। यह स्नान जल में पीले चन्दन के मिश्रण से किया जाता है, जो शरीर और मन को शुद्ध एवं पवित्र करता है।",
            en: "After performing the Maha Abhishek with the Rudra Sukta, we shall now perform the Gandhodak Snan for Lord Shiva. This involves bathing with water mixed with yellow sandalwood paste, symbolizing purity and sanctity, and purifying both body and mind."
        },
        mantras: [
            {
                mantra: {
                    hi: "मलयाचलसम्भूतं चन्दनेन विमिश्रितं। इदं गंधोदकस्नानं कुंकुमाक्तं नु गृह्यताम्॥ ॥ ओं साम्ब सदाशिवाय नमः इदं गंधोदकस्नानं समर्पयामि॥",
                    en: "Malayāchalasambhūtaṃ Candanena Vimiśritaṃ; Idaṃ Gandhodakasnānaṃ Kuṅkumāktaṃ Nu Gṛhyatām. Om Sāmb Sadāśivāya Namaḥ, Idaṃ Gandhodakasnānaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे शम्भू! मलयाचल से प्राप्त सुगंधित चन्दन को जल में मिलाकर किया गया यह स्नान शरीर और मन को शुद्ध करता है। कृपया इस स्नान को स्वीकार करें।",
                    en: "O Shambhu! This bath with sandalwood paste obtained from the fragrant Malaya Mountain, mixed with water, purifies the body and mind. Please accept this offering."
                }
            }
        ]
    },
    {
        id: 25,
        title: {
            hi: "शुद्धोदक स्नान",
            en: "Purified Water Bath (Shuddhodaka Snan)"
        },
        description: {
            hi: "गंधोदक स्नान के पश्चात अब भगवान शिव का शुद्धोदक स्नान किया जाएगा। यह स्नान शुद्ध और शीतल जल से किया जाता है, जो भगवान शिव द्वारा हलाहल विष के पान के पश्चात शांति और संतुलन का प्रतीक है।",
            en: "After the Gandhodak Abhishek, we now perform the Shuddhodak Abhishek of Lord Shiva. This ritual bath uses pure and cool water symbolizing calmness and purity, offered to soothe and balance the divine energy after Lord Shiva consumed the Halahala poison."
        },
        mantras: [
            {
                mantra: {
                    hi: "ॐ शुद्धवालः सर्वशुद्धवालो मणिवालस्त आश्विनाः। श्वेतः श्वेताक्षोऽरुणस्ते रूद्राय पशुपतये कर्णा यामा अवलिप्ता रौद्रा नभोरूपाः पार्जन्याः॥ ॥ ओं साम्ब सदाशिवाय नमः इदं शुद्धोदकस्नानं समर्पयामि॥",
                    en: "Om Śuddhavālaḥ Sarvaśuddhavālo Maṇivālast Āśvināḥ; Śvetaḥ Śvetākṣoʾruṇaste Rūdrāya Paśupataye Karṇā Yāmā Avaliptā Raudrā Nabhorūpāḥ Pārjanyāḥ. Om Sāmb Sadāśivāya Namaḥ, Idam Śuddhodaka Snānam Samarpayāmi."
                },
                meaning: {
                    hi: "ॐ — जो पूर्णतः शुद्ध हैं, सर्वशुद्ध प्रदान करने वाले हैं और रत्नों के समान तेजस्वी हैं, वे अश्विनि देवता हैं। जो श्वेतवर्ण और श्वेत नेत्र वाले हैं, अरुण तेज से युक्त हैं और रुद्रस्वरूप पशुपति के सेवक हैं। वे दिग्देवता रौद्र भाव से युक्त, आकाश रूपधारी और पार्जन्य (वर्षा) समान हैं। हे शिव! मैं यह शुद्ध और शीतल जल आपके स्नान के लिए अर्पित करता हूँ।",
                    en: "Om — The all-pure, bestower of supreme purity, radiant as jewels — the Ashvini deities! White-bodied and white-eyed, with reddish brilliance, attendants of Rudra, the Lord of all beings. Fierce yet pure, embodying the sky and the rain-bearing clouds — O Shiva, accept this pure and cool water offered for Your divine bath."
                }
            }
        ]
    },
    {
        id: 26,
        title: {
            hi: "वस्त्र",
            en: "Cloth (Vastra)"
        },
        description: {
            hi: "शुद्धोदक स्नान के पश्चात अब भगवान शिव को वस्त्र अर्पित किए जाते हैं। वस्त्र श्वेत या बाघाम्बर होने चाहिए, क्योंकि ये भगवान शिव के प्रिय वस्त्र माने गए हैं।",
            en: "After the Shuddhodak Abhishek, garments (Vastra) are offered to Lord Shiva. The garments should be white or tiger-skin patterned (Bagambar), as these are considered most auspicious and beloved to Lord Shiva."
        },
        mantras: [
            {
                mantra: {
                    hi: "शीतवातोष्णसंत्राणं लज्जायां रक्षणं परम्। देहालंकारणं वस्त्रमतः शान्तिप्रयक्षमे॥ ॥ ओं साम्ब सदाशिवाय नमः वस्त्रोपवस्त्रं समर्पयामि॥",
                    en: "Śītavātōṣṇasantrāṇam Lajjāyāṃ Rakṣaṇaṃ Param; Dehālaṃkāraṇaṃ Vastramataḥ Śānti Prayakṣame. Om Sāmb Sadāśivāya Namaḥ, Vastrōpavastraṃ Samarpayāmi."
                },
                meaning: {
                    hi: "वस्त्र ठंड, हवा और गर्मी से शरीर की रक्षा करता है, लज्जा की रक्षा करता है तथा शरीर को अलंकृत करता है। यह शांति प्रदान करने वाला माना गया है। हे प्रभु, कृपया इसे स्वीकार करें और सदा मुझ पर प्रसन्न रहें।",
                    en: "Clothing protects the body from cold, heat, and wind, preserves modesty, and adorns the body. It is regarded as a source of peace and comfort. O Lord, please accept these garments and remain ever pleased with me."
                }
            }
        ]
    },
    {
        id: 27,
        title: {
            hi: "यज्ञोपवीत",
            en: "Sacred Thread (Yagyopavit)"
        },
        description: {
            hi: "वस्त्र अर्पण के पश्चात अब भगवान शिव को यज्ञोपवीत अर्पित किया जाता है। यज्ञोपवीत, जिसे उपवीत भी कहा जाता है, पवित्रता और आध्यात्मिक अनुशासन का प्रतीक है।",
            en: "After offering the garments, the Yajnopavita (sacred thread) is offered to Lord Shiva. The Yajnopavita, also known as Upavita, symbolizes purity and spiritual discipline."
        },
        mantras: [
            {
                mantra: {
                    hi: "ॐ यज्ञोपवीतं परमं पवित्रं, प्रजापतेर्यत्सहजं पुरस्तात्। आयुष्यमग्र्यं प्रतिमुञ्च शुभ्रं, यज्ञोपवीतं बलमस्तु तेजः। ॥ ओं साम्ब सदाशिवाय नमः यज्ञोपवीतं समर्पयामि॥",
                    en: "Om Yajñopavītaṃ Paramaṃ Pavitraṃ, Prajāpater Yatsahajaṃ Purastāt; Āyuṣyamagryaṃ Pratimuñca Śubhraṃ, Yajñopavītaṃ Balamastu Tejaḥ. Om Sāmb Sadāśivāya Namaḥ, Yajñopavītaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "ॐ यज्ञोपवीत अत्यंत पवित्र है, जो प्रजापति से स्वाभाविक रूप में उत्पन्न हुआ है। यह आयु, बल और तेज प्रदान करने वाला है। हे शुभ्र (सफेद) यज्ञोपवीत! मैं तुम्हें अर्पित करता हूँ, तुम मेरे लिए दीर्घायु, शक्ति और तेज के दाता बनो। हे महादेव, कृपया इस यज्ञोपवीत को स्वीकार करें।",
                    en: "Om, the sacred thread (Yajnopavit) is supremely pure, naturally originating from Prajapati. It bestows longevity, strength, and brilliance. O pure Yajnopavita! I offer you to the Lord with devotion — be the giver of long life, power, and radiance. O Mahadeva, please accept this sacred thread."
                }
            }
        ]
    },
    {
        id: 28,
        title: {
            hi: "गंधम्",
            en: "Fragrance (Gandham)"
        },
        description: {
            hi: "यज्ञोपवीत अर्पण के पश्चात अब भगवान शिव को पीले और लाल चंदन से बना गंध अर्पित किया जाएगा, जो सुगंध और पवित्रता का प्रतीक है।",
            en: "After offering the Yajnopavita, we shall now offer Lord Shiva the sacred fragrance made from yellow and red sandalwood, symbolizing purity and devotion."
        },
        mantras: [
            {
                mantra: {
                    hi: "गन्धं गृहाण देवेश कस्तूरी कुङ्कुमान्वितम्। विलेपनार्थं कर्पूर रोचन लोहितं मया॥ ॥ ओं साम्ब सदाशिवाय नमः इदं गन्धं समर्पयामि॥",
                    en: "Gandhaṃ Gṛhāṇa Deveśa Kastūri Kuṅkumānvitam; Vilepanārthaṃ Karpūra Rokana Lohitaṃ Mayā. Om Sāmb Sadāśivāya Namaḥ, Idaṃ Gandhaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे देवेश साम्ब सदाशिव! कस्तूरी और कुंकुम से युक्त यह सुगंधित चंदन मैं आपको अर्पित करता हूँ। इसमें कपूर और रोचन का सुगंध मिश्रित है। हे महादेव! यह गंध विलेपन के लिए समर्पित है, कृपया इसे स्वीकार करें।",
                    en: "O Lord Devesha Samba Sadashiva! I offer You this fragrant sandal paste blended with musk and kumkum. It also contains camphor and rochan (a yellow aromatic substance). O Mahadeva! This offering of fragrance is meant for Your divine anointment — please accept it."
                }
            }
        ]
    },
    {
        id: 29,
        title: {
            hi: "कुङ्कुम",
            en: "Vermilion (Kunkum)"
        },
        description: {
            hi: "पीले और लाल चंदन अर्पण के पश्चात अब भगवान शिव को लाल कुङ्कुम अर्पित किया जाएगा। कुङ्कुम का प्रयोग भगवान के अलंकरण और सजावट के लिए किया जाता है।",
            en: "After offering the yellow and red sandalwood, red kumkum is offered to Lord Shiva, which is used for adorning and decorating the deity."
        },
        mantras: [
            {
                mantra: {
                    hi: "कुङ्कुमं कामदं दिव्यं कामिनी काम सम्भवम्। कुङ्कुमार्चितं देव प्रसीद शिव शंकर॥ ॥ ओं साम्ब सदाशिवाय नमः इदं कुङ्कुमं समर्पयामि॥",
                    en: "Kuṅkumaṃ Kāmadaṃ Divyaṃ Kāminī Kāma Sambhavam; Kuṅkumārcitaṃ Deva Prasīda Śiva Śaṅkara. Om Sāmb Sadāśivāya Namaḥ, Idaṃ Kuṅkumaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे देवाधिदेव महादेव शंकर! यह दिव्य कुङ्कुम, जो कामदेव और कामिनी से उत्पन्न हुआ है, कामना पूर्ण करने वाला है। हे शिव शंकर! यह कुङ्कुम आपको अर्पित है — कृपया इसे स्वीकार करें और प्रसन्न होकर अपनी कृपा प्रदान करें।",
                    en: "O Lord of the Gods, Mahadev Shankar! This divine kumkum, born of the God of Love and Kamini, fulfills desires. O Shiva Shankar, this kumkum is offered to You — may You accept it, be pleased, and bless us with Your grace."
                }
            }
        ]
    },
    {
        id: 30,
        title: {
            hi: "भस्म",
            en: "Sacred Ash (Bhasma)"
        },
        description: {
            hi: "लाल कुङ्कुम अर्पण के पश्चात अब भगवान शिव को भस्म अर्पित किया जाता है। भगवान शिव सदैव भस्म से विभूषित रहते हैं, जो पवित्रता और पापों के विनाश का प्रतीक है।",
            en: "After offering red Kumkum, Bhasma (sacred ash) is offered to Lord Shiva. Lord Shiva is always adorned with Bhasma, representing purity and the destruction of all sins."
        },
        mantras: [
            {
                mantra: {
                    hi: "सर्वपापहरं भस्म दिव्यज्योतिसमप्रभम्। सर्वक्षेमकरं पुण्यं गृहाण परमेश्वर॥ ॥ ओं साम्ब सदाशिवाय नमः भस्मं समर्पयामि॥",
                    en: "Sarvapāpaharaṃ Bhasma Divyajyotisamaprabham; Sarvakshemakaraṃ Puṇyaṃ Gṛhāṇa Parameśvara. Om Sāmb Sadāśivāya Namaḥ, Bhasmaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "यह भस्म सभी पापों का नाश करने वाली, दिव्य ज्योति के समान तेजस्वी और सम्पूर्ण कल्याण देने वाली है। हे परमेश्वर! कृपया इस पवित्र भस्म को स्वीकार करें और अपनी कृपा प्रदान करें।",
                    en: "This sacred ash destroys all sins, shines like divine radiance, and grants complete well-being and merit. O Supreme Lord! Kindly accept this holy ash and bestow Your grace upon us."
                }
            }
        ]
    },
    {
        id: 31,
        title: {
            hi: "अक्षत",
            en: "Unbroken Rice Grains (Akshata)"
        },
        description: {
            hi: "भस्म अर्पण के पश्चात अब भगवान शिव को अक्षत (अखंड चावल के दाने) अर्पित किए जाते हैं। अक्षत शुद्धता, भक्ति और शुभता का प्रतीक हैं।",
            en: "After offering Bhasma, Akshata (unbroken rice grains) are offered to Lord Shiva. Akshata symbolize purity, devotion, and auspiciousness."
        },
        mantras: [
            {
                mantra: {
                    hi: "अक्षताश्च सुरश्रेष्ठ कुङ्कुमाक्ताः सुशोभिता:. मया निवेदिता भक्त्याः गृहाण परमेश्वर॥ ॥ ओं साम्ब सदाशिवाय नमः अक्षतान् समर्पयामि॥",
                    en: "Akṣatāśca Suraśreṣṭha Kuṅkamāktāḥ Suśobhitāḥ; Mayā Niveditā Bhaktyāḥ Gṛhāṇa Parameśvara. Om Sāmb Sadāśivāya Namaḥ, Akṣatān Samarpayāmi."
                },
                meaning: {
                    hi: "हे देवों में श्रेष्ठ परमेश्वर! कुंकुम से युक्त, सुंदर और चमकदार ये अक्षत (चावल के दाने) मैं भक्ति भाव से आपको अर्पित करता हूँ, कृपया इन्हें स्वीकार करें।",
                    en: "O Supreme Lord among the gods! These rice grains, bright and beautiful, anointed with kumkum, I offer to You with devotion. Please accept them graciously."
                }
            }
        ]
    },
    {
        id: 32,
        title: {
            hi: "पुष्प",
            en: "Flowers (Pushpa)"
        },
        description: {
            hi: "अक्षत अर्पण के पश्चात अब भगवान शिव को पुष्प अर्पित किए जाते हैं। भगवान शिव को जाती, चम्पक और श्वेत पुष्प विशेष प्रिय हैं और ये अत्यंत शुभ माने जाते हैं।",
            en: "After offering Akshat, flowers are offered to Lord Shiva. Lord Shiva is especially fond of Jati (Jasmine), Champak, and white flowers, all considered highly auspicious for Him."
        },
        mantras: [
            {
                mantra: {
                    hi: "माल्यादीनि सुगन्धीनि मालत्यादीनि वै प्रभो। मयानीतानि पुष्पाणि पूजार्थं प्रतिगृह्यताम्॥ ॥ ओं साम्ब सदाशिवाय नमः इदं पुष्पं समर्पयामि॥\nबिल्वापामार्गधत्तूरकरवीरार्कसंभवैः। बकोत्फलद्रोणमुख्यैः पुष्पैः पूजितशङ्कर॥ ॥ ओं साम्ब सदाशिवाय नमः इदं पुष्पं समर्पयामि॥",
                    en: "Mālyādīni Sugandhīni Mālatyādīnī Vai Prabho; Mayānītāni Puṣpāṇi Pūjārthaṃ Pratigṛhyatām. Om Sāmb Sadāśivāya Namaḥ, Idaṃ Puṣpaṃ Samarpayāmi.\nBilvāpāmārga Dhattūra Karavīrārka Sambhavaiḥ; Bakotphaladroṇa Mukhyaiḥ Puṣpaiḥ Pūjita Śaṅkara. Om Sāmb Sadāśivāya Namaḥ, Idaṃ Puṣpaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे प्रभु! मालती सहित सुगंधित मालाएँ और फूल मैं भक्ति भाव से आपके पूजन के लिए लाया हूँ, कृपया इन्हें स्वीकार करें। हे शङ्कर! बिल्व, आम, धत्तूरा, करवीर, अर्क, बक का पुष्प और अन्य प्रमुख पुष्पों से बनी यह पुष्पमालिका आपको अर्पित है। इस पुष्प अर्पण के द्वारा हम आपकी पूजा करते हैं और आपके कृपा एवं आशीर्वाद की प्रार्थना करते हैं।",
                    en: "O Lord! Fragrant garlands and flowers, including jasmine, are brought by me in devotion for Your worship — please accept them. O Shankara! This bouquet, made from Bilva leaves, mango flowers, Dhatūra, Karvira (oleander), Arka, Bak flowers, and other prominent blossoms, is humbly offered to You. Through this offering, we worship You and seek Your blessings, grace, and compassionate gaze."
                }
            }
        ]
    },
    {
        id: 33,
        title: {
            hi: "बिल्वपत्र अर्पण",
            en: "Offering of Bilva Leaves (Bilvapatra Arpan)"
        },
        description: {
            hi: "पुष्प अर्पण के पश्चात अब भगवान शिव को बिल्वपत्र अर्पित किए जाते हैं। ऐसा कहा गया है कि बिल्वपत्र के बिना भगवान शिव की पूजा अधूरी रहती है। बिल्वपत्र को चंदन से ‘शिव’ लिखकर उल्टा चढ़ाया जाता है, और उसमें मां गौरी का वास भी माना जाता है।",
            en: "After offering flowers, Bilva leaves (Bel Patra) are offered to Lord Shiva. It is said that the worship of Lord Shiva is incomplete without Bilva leaves. These leaves are marked with the word ‘Shiva’ using sandalwood paste and are offered inverted, as Goddess Gauri is believed to reside within them."
        },
        mantras: [
            {
                mantra: {
                    hi: "नमो बिल्मिने च कवचिने च नमो वर्म्मिणे च वरूथिने च नमः श्रुताय च श्रुतसेनाय च नमो दुन्दुभ्याय चा हनन्न्याय च नमो घृश्णवे॥ ॥ बिल्व पत्रं समर्पयामि॥",
                    en: "Namo Billmine Cha Kavchine Cha Namo Varmmine Cha Varūthine Cha; Namaḥ Śrutāya Cha Śrutasenāya Cha Namo Dundubhyāya Chā Hanannyāya Cha Namo Ghṛśṇave. Bilva Patraṃ Samarpayāmi."
                },
                meaning: {
                    hi: "भगवान शिव के विविध रूपों और शक्तियों को नमस्कार — जो कवचधारी, वर्मधारी, वरूथी, श्रुतसेना, दुन्दुभि धारी और घृष्णु (सर्वनियामक) हैं। हे नीलकंठ! यह पवित्र बिल्वपत्र आपको समर्पित है, कृपया इसे स्वीकार करें और कल्याण प्रदान करें।",
                    en: "Salutations to the many forms and powers of Lord Shiva — the armored one, the protector, the defender, the leader of divine armies, the bearer of the battle drum, and the all-governing one. O Neelkantha, I offer this sacred Bilva leaf to You; please accept it and shower Your blessings upon us."
                }
            },
            {
                mantra: {
                    hi: "त्रिदलं त्रिगुणाकारं त्रिनेत्रं च त्रिधायुधम् । त्रिजन्मपापसंहारं बिल्वपत्रं शिवार्पणम् बिल्व पत्रं समर्पयामि",
                    en: "Tridalaṃ trigunākāraṃ trinetraṃ cha tridhāyudham। Trijannapāpasamhāraṃ bilvapatraṃ śivārpaṇam। Bilva patraṃ samarpayāmi॥"
                },
                meaning: {
                    hi: "विल्वपत्र त्रिदली, त्रिगुणाकार और त्रिनेत्री शिव के त्रिधा आयुधों का प्रतीक है। तीन जन्मों के पापों का नाश विल्वपत्र अर्पण से होता है।",
                    en: "The Bilva leaf, with its three leaflets, symbolizes the three qualities (gunas) and the three-eyed Shiva’s threefold weapons. Offering the Bilva leaf leads to the destruction of sins committed over three lifetimes."
                }
            }
        ]
    },
    {
        id: 34,
        title: {
            hi: "धूप",
            en: "Incense (Dhoop)"
        },
        description: {
            hi: "विल्वपत्र अर्पण के पश्चात अब भगवान शिव को धूप अर्पित की जाती है। धूप सुगंध और पवित्रता हेतु अर्पित की जाती है जिससे पूजन स्थल दिव्यता और माधुर्य से परिपूर्ण हो जाता है।",
            en: "After offering Bilva leaves, incense (Dhoop) is offered to Lord Shiva. Incense is offered to spread sacred fragrance and create an atmosphere of purity and divinity during the worship."
        },
        mantras: [
            {
                mantra: {
                    hi: "वनस्पत्युद्भवो दिव्यो गन्धाढ्यो गन्धोत्तमः। आघ्रेयः महिपालो धूपोऽयं प्रतिगृह्यताम्॥ ॥ ओं साम्ब सदाशिवाय नमः धूपं समर्पयामि॥",
                    en: "Vanaspatiyudbhavo Divyo Gandhāḍhyo Gandhottamaḥ; Āghreyaḥ Mahipālo Dhūpo’yaṃ Pratigṛhyatām. Om Sāmb Sadāśivāya Namaḥ, Dhūpaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे महेश्वर! यह धूप वनस्पति से उत्पन्न, दिव्य, सुगंधित और श्रेष्ठतम गंध वाला है। यह आपके पूजन के लिए अर्पित किया गया है। कृपया इस धूप को और हमारी भक्ति को स्वीकार करें।",
                    en: "O Maheshwara! This incense, derived from trees and plants, divine and filled with the finest fragrance, is offered to You for worship. O Lord, kindly accept this incense and our devotion with grace."
                }
            }
        ]
    },
    {
        id: 35,
        title: {
            hi: "दीप",
            en: "Lamp (Deep)"
        },
        description: {
            hi: "धूप अर्पण के पश्चात अब भगवान शिव को दीप अर्पित किया जाता है। दीप प्रकाश, पवित्रता और ईश्वरीय उपस्थिति का प्रतीक है।",
            en: "After offering incense, the sacred lamp (Deep) is offered to Lord Shiva, symbolizing light, purity, and divine presence."
        },
        mantras: [
            {
                mantra: {
                    hi: "दीपं हि परमं शम्भो घृत प्रज्वलितं मया। दत्तं गृहाण देवेश मम ज्ञानप्रद भव॥ त्राहि मां नरकाद् घोराद् दीपज्योतिर्नमोऽस्तुते॥ ॥ ओं साम्ब सदाशिवाय नमः दीपं समर्पयामि॥",
                    en: "Dīpaṃ Hi Paramaṃ Śambho Ghṛta Prajvalitaṃ Mayā; Dattaṃ Gṛhāṇa Deveśa Mama Jñānaprada Bhava. Trāhi Māṃ Narakād Ghorād Dīpajyotir Namo’stute. Om Sāmb Sadāśivāya Namaḥ, Dīpaṃ Samarpayāmi."
                },
                meaning: {
                    hi: "हे शम्भो! यह दीप, जो घृत से प्रज्वलित है, मैं आपको अर्पित करता हूँ। हे देवेश! इसे स्वीकार करें और मुझे ज्ञान तथा बुद्धि प्रदान करें। हे दीपज्योति! मुझे अंधकार, पाप और नरक की भयानक पीड़ा से बचाएँ।",
                    en: "O Shambho! This lamp, lit with ghee, I offer to You. O Divine Lord! Accept it and grant me the gift of knowledge and wisdom. O sacred lamp-light! Protect me from darkness, sin, and the dreadful torments of hell."
                }
            }
        ]
    },
    {
        id: 36,
        title: {
            hi: "नैवेद्य",
            en: "Offering of Food (Naivedya)"
        },
        description: {
            hi: "दीप अर्पण के बाद अब भगवान शिव को नैवेद्य अर्पित किया जाता है। नैवेद्य भक्ति और श्रद्धा से अर्पित किया गया दिव्य भोजन है, जिसे भगवान प्रसन्नतापूर्वक ग्रहण करते हैं। इसके बाद पंचप्राण से युक्त जल द्वारा भगवान को शान्त किया जाता है — प्राण, व्यान, अपान, समान, और उदान।",
            en: "After offering the lamp, Naivedya (sacred food) is offered to Lord Shiva. Naivedya is the divine food offered with devotion and reverence, which the Lord accepts graciously. After this, the Lord is calmed and purified with water dedicated to the five vital airs — Prana, Vyana, Apana, Samana, and Udana."
        },
        mantras: [
            {
                mantra: {
                    hi: "शर्करा-खण्ड-खाद्यानि दधि-क्षीर-घृतानि च आहारं भक्ष्य भोज्यं च नैवेद्यं प्रतिगृह्यताम्॥\nॐ प्राणाय स्वाहा\nॐ व्यानाय स्वाहा\nॐ अपनाय स्वाहा\nॐ समानाय स्वाहा\nॐ उदानाय स्वाहा\nश्री सदाशिवं नमस्तुभ्यं महानैवेद्यमुत्तमम्। संगृहाण सुरश्रेष्ठ भक्ति-मुक्ति प्रदायकम्॥\nएतानि नानाविध नैवेद्यानि समर्पयामि ॥ ओं साम्ब सदाशिवाय नमः॥",
                    en: "Śarkarā-khaṇḍa-khādyāni Dadhi-kṣīra-ghṛtāni Ca Āhāraṃ Bhakṣya Bhojyaṃ Ca Naivedyaṃ Pratigṛhyatām.\nOm Prāṇāya Svāhā\nOm Vyānāya Svāhā\nOm Apanāya Svāhā\nOm Samānāya Svāhā\nOm Udānāya Svāhā\nŚrī Sadāśivaṃ Namastubhyaṃ Mahānaivedyamuttamam, Saṃgṛhāṇa Surasattama Bhakti-Mukti Pradāyakam.\nEtāni Nānavidhanaivedyāni Samarpayāmi. Om Sāmb Sadāśivāya Namaḥ."
                },
                meaning: {
                    hi: "हे प्रभु! शर्करा, खण्ड, दही, दूध, घृत आदि से बने भक्ष्य एवं भोज्य पदार्थों को मैं आपको नैवेद्य रूप में श्रद्धापूर्वक अर्पित करता हूँ।\nॐ — मैं पंचप्राण — प्राण, व्यान, अपान, समान और उदान — को नमन करते हुए यह नैवेद्य अर्पित करता हूँ। यह महान और उत्तम नैवेद्य, जो भक्ति व मुक्ति प्रदान करता है, कृपया स्वीकार करें।",
                    en: "O Lord! Sugar, jaggery pieces, curd, milk, ghee, and other edible foods are devotedly offered to You as Naivedya.\nOm — bowing to the five vital airs — Prana, Vyana, Apana, Samana, and Udana — I offer this Naivedya. This supreme and excellent Naivedya, the giver of devotion and liberation, please accept it, O Supreme Deity."
                }
            }
        ]
    },
    {
        id: 37,
        title: {
            hi: "तांबूलं",
            en: "Tambulam"
        },
        description: {
            hi: "नैवेद्य अर्पण के पश्चात भगवान शिव को तांबूल अर्पित किया जाता है। यह सुपारी, पान के पत्ते और सुगंधित मसालों का दिव्य संयोजन होता है।",
            en: "After offering Naivedya, Tāmbula — a divine combination of betel leaves, betel nuts, and fragrant spices — is offered to Lord Shiva."
        },
        mantras: [
            {
                mantra: {
                    hi: "पूगीफलम् महद्दिव्यं नागवेल्ली दलैर्युतम्। एलादि चूर्ण संयुक्तं तांबूलं प्रतिगृह्यताम्॥",
                    en: "Pūgīphalaṃ Mahaddivyaṃ Nāgavallī Dalairyutam; Elādi Chūrṇa Saṃyuktaṃ Tāmbūlaṃ Pratigṛhyatām."
                },
                meaning: {
                    hi: "हे प्रभु! यह उत्तम दिव्य सुपारी (पूगीफल), पान के पत्तों और इलायची आदि सुगंधित चूर्ण से युक्त तांबूल (पान) आपको अर्पित करता हूँ। कृपया इसे स्वीकार करें।",
                    en: "O Lord! This excellent divine betel nut (Pūgi fruit), combined with betel leaves and aromatic powders like cardamom, is offered to You as Tāmbula. Please accept it graciously."
                }
            }
        ]
    },
    {
        id: 38,
        title: {
            hi: "फलार्पणम्",
            en: "Offering of Fruits (Phalarpanam)"
        },
        description: {
            hi: "तांबूल अर्पण के पश्चात भगवान शिव को ऋतु के अनुसार फलों का अर्पण किया जाता है। ये फल समृद्धि, शुभता और कल्याण के प्रतीक हैं और इन्हें पूर्ण श्रद्धा से भगवान को समर्पित किया जाता है।",
            en: "After offering Tāmbula, seasonal fruits are offered to Lord Shiva. These fruits symbolize prosperity, auspiciousness, and well-being and are presented to the Lord with devotion."
        },
        mantras: [
            {
                mantra: {
                    hi: "इदं फलं मयादेव स्थापितं पुरतस्तव ।  तेन मे सफलावाप्तिर्भवेत् जन्मनि जन्मनि ॥ एतानि ऋतुफलानि समर्पयामि ओं साम्ब सदाशिवाय नम",
                    en: "Idam phalam maya dev sthapitam puratastav. Tena me saphalavaptir bhavet janmani janmani. Etani rituphalani samarpayami Om Samb Sadashivaya Namah."
                },
                meaning: {
                    hi: "हे देव! यह फल जो मैंने आपके समक्ष स्थापित किए हैं, कृपया इन्हें स्वीकार करें। इन फलों के अर्पण से मुझे जन्म-जन्मांतर तक सफलता, समृद्धि और कल्याण प्राप्त हो। इसमें कूष्माण्ड (कद्दू), मातुलिंग (तरबूज/नींबू जातीय फल) और नारियल आदि फल सम्मिलित हैं। हे पार्वतीप्रिय सोमेश्वर! कृपया इसे ग्रहण करें।",
                    en: "O Lord! These fruits that I have respectfully placed before You — please accept them. May this offering of fruits bring me success, prosperity, and well-being across many lifetimes. These include Kushmanda (pumpkin), Matulinga (citrus or watermelon), and coconut among others. O Someshwara, beloved of Parvati — kindly accept this offering."
                }
            }
        ]
    },
    {
        id: 39,
        title: {
            hi: "शिव आरती",
            en: "Shiva Aarti"
        },
        description: {
            hi: "समस्त प्रकार की सामग्री अर्पण करने के बाद भगवान शिव की आरती की जाती है। यह आरती पूजा में हुई किसी भी कमी या त्रुटि को दूर करने, सभी दोषों के निवारण और मनोकामनाओं की पूर्ति के लिए की जाती है। भक्त ध्यानपूर्वक आरती सुनें और पुण्य प्राप्त करें।" + "\n\n**निर्देश:**\n**तो आइये भक्तों भगवान शिव की आरती करते हैं , आप ध्यान पूर्वक भगवान शिव की आरती को सुने और पुण्य को प्राप्त करें ।**",
            en: "After offering all the materials, the Aarti of Lord Shiva is performed. This ritual helps remove any shortcomings or errors in the worship, resolves all faults, and fulfills heartfelt desires. Devotees should listen attentively to gain divine blessings and merit." + "\n\n**Instructions:**\n**So, dear devotees, let us perform the Aarti of Lord Shiva. Please listen attentively and receive the divine blessings and merit from this sacred ritual.**"
        },
        mantras: [
            {
                mantra: {
                    hi: "जय शिव ओमकारा प्रभु हर शिव ओमकारा\nब्रह्मा विष्णु सदाशिव ब्रह्मा विष्णु सदाशिव\nअर्द्धांगी धारा\nॐ जय शिव ओमकारा\nजय शिव ओमकारा प्रभु हर शिव ओमकारा\nब्रह्मा विष्णु सदाशिव ब्रह्मा विष्णु सदाशिव\nअर्द्धांगी धारा\nॐ जय शिव ओमकारा\nएकानन चतुरानन पञ्चानन राजे\nस्वामी पञ्चानन राजे\nहंसासन गरूड़ासन\nहंसासन गरूड़ासन\nवृषवाहन साजे\nॐ जय शिव ओमकारा\nदो भुज चार चतुर्भुज दसभुज अति सोहे\nस्वामी दसभुज अति सोहे\nतीनो रूप निरखता\nतीनो रूप निरखता\nत्रिभुवन जन मोहे\nॐ जय शिव ओमकारा\nअक्षमाला वनमाला मुण्डमाला धारी\nस्वामी मुण्डमाला धारी\nत्रिपुरारी कंसारी\nकर माला धारी\nॐ जय शिव ओमकारा\nश्वेताम्बर पीताम्बर बाघम्बर अंगे\nस्वामी बाघम्बर अंगे\nसनकादिक ब्रम्हादिक\nब्रम्हादिक सनकादिक\nभूतादिक संगे\nॐ जय शिव ओमकारा\nकर के मध्य कमण्डलु चक्र त्रिशूलधारी\nस्वामी चक्र त्रिशूलधारी\nजगहर्ता जगकर्ता\nजगहर्ता जगकर्ता\nजगपालन कारी\nॐ जय शिव ओमकारा\nब्रह्मा विष्णु सदाशिव जानत अविवेका\nस्वामी जानत अविवेका\nप्रानवाक्षर के मध्ये(प्रानवाक्षर के मध्ये)\nये तीनो के धार\nॐ जय शिव ओमकारा\nत्रिगुणस्वामी जी की आरति जो कोइ नर गावे\nस्वामी जो कोइ नर गावे\nकहत शिवानन्द स्वामी\nकहत शिवानन्द स्वामी\nमनवान्छित फल पावे\nॐ जय शिव ओमकारा\nजय शिव ओमकारा प्रभु हर शिव ओमकारा\nब्रह्मा विष्णु सदाशिव ब्रह्मा विष्णु सदाशिव\nअर्द्धांगी धारा\nॐ जय शिव ओमकारा\nजय शिव ओमकारा प्रभु हर शिव ओमकारा\nब्रह्मा विष्णु सदाशिव ब्रह्मा विष्णु सदाशिव\nअर्द्धांगी धारा\nॐ जय शिव ओमकारा\n",
                    en: "Jai Shiv Omkara Prabhu Har Shiv Omkara\nBrahma Vishnu Sadashiv Brahma Vishnu Sadashiv\nArdhangi Dhara\nOm Jai Shiv Omkara\nJai Shiv Omkara Prabhu Har Shiv Omkara\nBrahma Vishnu Sadashiv Brahma Vishnu Sadashiv\nArdhangi Dhara\nOm Jai Shiv Omkara\nEkanan Chaturanand Panchanan Raje\nSwami Panchanan Raje\nHansasan Garudasan\nHansasan Garudasan\nVrishvahan Saje\nOm Jai Shiv Omkara\nDo Bhuj Char Chaturbhuj Dasbhuj Ati Sohe\nSwami Dasbhuj Ati Sohe\nTeeno Roop Nirakhta\nTeeno Roop Nirakhta\nTribhuvan Jan Mohe\nOm Jai Shiv Omkara\nAkshamala Vanmala Mundmala Dhari\nSwami Mundmala Dhari\nTripurari Kansari\nKar Mala Dhari\nOm Jai Shiv Omkara\nShvetambar Peetambar Baghambar Ange\nSwami Baghambar Ange\nSanakadik Brahmadik\nBrahmadik Sanakadik\nBhootadik Sange\nOm Jai Shiv Omkara\nKar Ke Madhya Kamandalu Chakra Trishuldhari\nSwami Chakra Trishuldhari\nJag Harta Jag Karta\nJag Harta Jag Karta\nJag Palan Kari\nOm Jai Shiv Omkara\nBrahma Vishnu Sadashiv Janat Aviveka\nSwami Janat Aviveka\nPranavakshar Ke Madhye (Pranavakshar Ke Madhye)\nYe Teeno Ke Dhar\nOm Jai Shiv Omkara\nTrigun Swami Ji Ki Aarti Jo Koi Nar Gave\nSwami Jo Koi Nar Gave\nKahat Shivanand Swami\nKahat Shivanand Swami\nManvanchhit Phal Pave\nOm Jai Shiv Omkara\nJai Shiv Omkara Prabhu Har Shiv Omkara\nBrahma Vishnu Sadashiv Brahma Vishnu Sadashiv\nArdhangi Dhara\nOm Jai Shiv Omkara\nJai Shiv Omkara Prabhu Har Shiv Omkara\nBrahma Vishnu Sadashiv Brahma Vishnu Sadashiv\nArdhangi Dhara\nOm Jai Shiv Omkara\n"
                },
                meaning: {
                    hi: "हे प्रभु शिव! आप ओंकारस्वरूप हैं। ब्रह्मा, विष्णु और सदाशिव के साथ अर्द्धांगिनी (माता पार्वती) के धारक हैं। आपके विभिन्न स्वरूपों में एकमुखी, चतुर्मुखी, पंचमुखी राजसी रूप शामिल हैं। आप हंस, गरुड़ और वृषभ वाहन धारण करते हैं। दो, चार और दस भुजाओं वाले रूप में आप त्रिगुण धारक हैं, जिनका दर्शन त्रिभुवन को मोहित करता है। आप अक्षमाला, वनमाला और रुण्डमाला (मुण्डमाला) धारण करते हैं। चन्दन, मृगमद और वन के पुष्पों से अलंकृत हैं। श्वेत, पीत और बाघाम्बर वस्त्र धारण करते हैं। आप सनक, ब्रह्मा और भूतगण के संग रहते हैं। करकमल में कमंडलु, चक्र और त्रिशूल धारण करते हैं। आप जगत के कारण, पालक और संहारक हैं। ब्रह्मा, विष्णु, सदाशिव के रूप में आप ही एक हैं और प्रणवाक्षर में शोभित हैं।",
                    en: "O Lord Shiva, embodiment of Omkara! You accompany Brahma, Vishnu, and Sadashiva alongside Your consort Parvati. You manifest as the one-faced, four-faced, and five-faced majestic forms. You ride upon the swan, Garuda, and the bull. Adorned with two, four, or ten arms, You embody the three gunas, captivating all worlds with Your vision. You wear garlands of beads, forest flowers, and skulls. You are beautified with sandal paste, musk, and forest blossoms. Draped in white, yellow, and tiger-skin vestments, You dwell among sages, Brahma, and celestial hosts. Holding the kamandalu, disc, and trident, You are the creator, sustainer, and destroyer of the universe. You are one with Brahma, Vishnu, and Sadashiva, glorifying the sacred Pranava syllable."
                }
            }
        ]
    },
    {
        id: 40,
        title: {
            hi: "कपूर आरती",
            en: "Camphor Aarti (Kapoor Aarti)"
        },
        description: {
            hi: "आरती के अंत में भगवान शिव को कर्पूर दीप अर्पित किया जाता है। यह आरती भगवान शिव की करुणामय, सर्वव्यापक और सर्वसाक्षी रूप में स्तुति करती है तथा भक्त के जीवन में समर्पण और एकत्व का भाव प्रकट करती है।",
            en: "At the conclusion of the Aarti, camphor flame is offered to Lord Shiva. This Aarti praises Lord Shiva as the embodiment of compassion and the eternal presence, expressing complete devotion and unity with the divine."
        },
        mantras: [
            {
                mantra: {
                    hi: "कर्पूरगौरं करुणावतारं संसारसारं भुजगेन्द्रहारम्। सदा वसन्तं हृदयारविन्दे भवं भवानीसहितं नमामि॥ त्वमेव माता च पिता त्वमेव त्वमेव बन्धुश्च सखा त्वमेव। त्वमेव विद्या द्रविणं त्वमेव त्वमेव सर्वं मम देव देव॥",
                    en: "Karpūragāuraṃ Karuṇāvatarāṃ Saṃsārasāraṃ Bhujagendrahāraṃ; Sadā Vasantam Hṛdayāravinde Bhavaṃ Bhavānīsahitaṃ Namāmi. Tvameva Mātā Ca Pitā Tvameva Tvameva Bandhuśca Sakhā Tvameva; Tvameva Vidyā Draviṇaṃ Tvameva Tvameva Sarvaṃ Mama Deva Deva."
                },
                meaning: {
                    hi: "मैं उस भगवान शिव को नमन करता हूँ जो कर्पूर के समान श्वेत हैं, करुणा के साक्षात अवतार हैं, जो संसार के सार और आधार हैं, जिनके गले में सर्पों की माला सुशोभित है, जो सदा भक्तों के हृदय रूपी कमल में विराजमान रहते हैं और अपनी अर्धांगिनी भवानी (पार्वती) सहित विराजते हैं। हे प्रभु! आप ही मेरे माता, पिता, बंधु, सखा, विद्या, धन और सर्वस्व हैं — हे देवों के देव, आप ही मेरे लिए सब कुछ हैं।",
                    en: "I bow to Lord Shiva, white as camphor and the very incarnation of compassion, who is the essence and foundation of the universe, adorned with a serpent garland, and who forever dwells in the lotus hearts of His devotees along with His beloved Bhavani (Parvati). O Lord! You are my mother, father, kin, and friend. You are my wisdom, my wealth, my everything — O God of gods, You are all to me."
                }
            }
        ]
    },
    {
        id: 41,
        title: {
            hi: "मंत्र पुष्पांजलि",
            en: "Mantra and Flower Offering (Mantra Pushpanjali)"
        },
        description: {
            hi: "आरती और कर्पूर आरती के पश्चात अब दोनों हाथों में पुष्प लेकर भगवान शिव को पुष्पांजलि अर्पित की जाती है। यह अर्पण समर्पण, पूर्णता और आभार का प्रतीक है।",
            en: "After performing the Aarti and the Camphor Aarti, devotees offer Pushpanjali — a floral offering symbolizing devotion, completion, and gratitude to Lord Shiva."
        },
        mantras: [
            {
                mantra: {
                    hi: "ॐ यज्ञेन यज्ञमयजन्त देवास्तनि धर्माणि प्रथमान्यासन् ।\nते ह नाकं महिमानः सचंत यत्र पूर्वे साध्याः संति देवाः ।।\nॐ राजाधिराजाय प्रसह्य साहिने ।\nनमो वयं वैश्रवणाय कुर्महे ।\nस मे कामान् कामकामाय मह्यं ।\nकामेश्वरो वैश्रवणो ददातु ।\nकुबेराय वैश्रवणाय महाराजाय नमः ।\nॐ स्वस्ति। साम्राज्यं भोज्यं स्वाराज्यं\nवैराज्यं पारमेष्ठ्यं राज्यं महाराज्यमाधिपत्यमयं\nसमंतपर्यायी स्यात् सार्वभैमः\nसार्वायुष आंतादापरात् ।।\nपृथिव्यै समुद्रपर्यन्ताया एकराळिति\nतदप्येषः श्लोकोऽभिगीतो मरूतः\nपरिवेष्टारो मरूतस्यावसन् गृहे ।।\nआविक्षितस्य कामप्रेर्विश्वेदेवाः सभासद इति ।।\nॐ एकदन्ताय विद्महे, वक्रतुण्डाय धीमहि, तन्नो दन्ति प्रचोदयात्॥\nॐ तत्पुरुषाय विद्महे महादेवाय धीमहि, तन्नो रुद्रः प्रचोदयात्॥\nॐ गिरिजायै च विद्महे शिवप्रियायै च धीमहि। तन्नो दुर्गा प्रचोदयात्॥\nसेवन्तिका बकुल चम्पक पाटलाब्जैः, पुन्नगजाति करवीर रसाल पुष्पैः।\nविल्व प्रबाल तुलसीदल मंजरीभिः, त्वां पूजयामि जगदीशवर! मे प्रसीद ।।",
                    en: "Om yagyen yagya mayajant devaas, tani dharmaani prathamaanyaasan.\nTe ha naakam mahimaanah sachant yatra purve saadhyaah santi devaah.\nOm Raajadhiraajaay prasahya saahine.\nNamo vayam Vaishravanaay kurmahe.\nSa me kaamaan kaamakaamaay mahyam.\nKaameshvaro Vaishravano dadaatu.\nKuberay Vaishravanaay Maharaajaay namah.\nOm Swasti. Saamraajyam bhojyam swaaraajyam\nVairaajyam paarameshthyam raajyam mahaaraajyam aadhipatya mayam\nSamantaparyaayi syaat saarvabhaumah\nSaarvaayush aantaadaaparaat.\nPrithivyai samudra paryantaaya ekaraal iti\nTadapyeshah shloko'bhigeeto marutah\nPariveshtaaro marutasyaavasan grihe.\nAavikshitasya kaamaprer vishve devaah sabhaasad iti.\nOm Ekadantaay vidmahe, vakratundaay dheemahi, tanno danti prachodayaat.\nOm Tatpurushaay vidmahe Mahadevaay dheemahi, tanno Rudrah prachodayaat.\nOm Girijaaye cha vidmahe Shivpriyaaye cha dheemahi, tanno Durga prachodayaat.\nSevantika, bakul, champak, paatlaabjai, punnag, jaati, karveer, rasaal pushpai,\nBilva prabaal, tulsi dal, manjariyon se,\nTvaam poojayaami Jagadishwar! Mere upar kripa karo."
                },
                meaning: {
                    hi: "देवताओं ने यज्ञ के द्वारा स्वयं यज्ञमय परमात्मा की आराधना कर सृष्टि के धर्मों की स्थापना की और स्वर्गलोक में स्थित दिव्य साध्यदेवों की तरह परम आवास प्राप्त किया।\nमैं वैश्रवण (कुबेर) महाराज को नमस्कार करता हूँ, जो इच्छाओं के स्वामी हैं और सभी कामनाएँ पूर्ण करने वाले हैं।\nहे एकदन्त गणेश! हमारी बुद्धि को शुभ दिशा में प्रेरित करें।\nहे तत्पुरुष महादेव! हमारी बुद्धि को धर्म और सत्य के मार्ग पर प्रेरित करें।\nहे गिरिजा माता, शिवप्रिया दुर्गा! हमारी बुद्धि को कल्याण और शक्ति की दिशा प्रदान करें।\nहे जगदीश्वर शिव! सेवन्तिका, बकुल, चम्पक, पाटल, कमल, पुन्नाग, जाति, करवीर और रसाल पुष्पों सहित, विल्वपत्र, तुलसी पत्र तथा उनकी मंजरी से आपका पूजन करता हूँ, कृपया प्रसन्न हों।",
                    en: "The gods worshipped the Lord of Sacrifice, the Yajnamaya Being, thereby establishing the foundations of universal righteousness (Dharma) and attaining the divine abode of the celestial sages.\nI bow to Kubera, the great King Vaishravana, the Lord of desires who grants fulfillment to all sincere wishes.\nO One-Tusked Ganesha! Inspire our minds toward virtue and goodness.\nO Tatpurusha Mahadeva! Enlighten our intellect with righteousness and truth.\nO Girija Devi, beloved of Shiva! May You guide our intellect toward welfare, strength, and spiritual power.\nO Lord of the Universe, Shiva! I worship You with flowers of Sevanthika, Bakula, Champaka, Patal, Kamal, Punnaga, Jati, Karvira, and Rasala, along with sacred Bilva and Tulsi leaves. O compassionate Lord, please be pleased with me."
                }
            }
        ]
    },
    {
        id: 42,
        title: {
            hi: "प्रदक्षिणा मंत्र",
            en: "Circumambulation Mantra (Pradakshina Mantra)"
        },
        description: {
            hi: "मंत्र पुष्पांजलि के बाद भगवान शिव की आधी परिक्रमा (अर्ध प्रदक्षिणा) की जाती है। शास्त्रों में विधान है कि भगवान शिव की हमेशा अर्ध प्रदक्षिणा ही होती है, जो भक्ति और श्रद्धा का प्रतीक है।" + "\n\n**निर्देश:**\n**प्रिय भक्तों, अपने स्थान पर बैठकर आपको भक्ति के प्रतीकात्मक संकेत के रूप में अपने हाथों को दक्षिणावर्त दिशा में अर्धवृत्ताकार घुमाना चाहिए।**",
            en: "After the Mantra Pushpanjali, the Ardha Parikrama (half circumambulation) of Lord Shiva is performed. Scriptures prescribe that Lord Shiva is always worshipped with a half circumambulation, symbolizing devotion and reverence." + "\n\n**Instructions:**\n**Dear devotees, while seated in your place, you should move your hands clockwise in a half-circle as a symbolic gesture of devotion.**"
        },
        mantras: [
            {
                mantra: {
                    hi: "यानि कानि च पापानि जन्मांतरकृतानि च। तानि सर्वाणि नश्यन्ति प्रदक्षिणा पदे पदे॥",
                    en: "Yāni Kāni Ca Pāpāni Janmāntara Kṛtāni Ca; Tāni Sarvāṇi Naśyanti Pradakṣiṇā Pade Pade."
                },
                meaning: {
                    hi: "हे भगवान! जो भी पाप मैंने इस जन्म और पिछले जन्मों में किए हैं, वे प्रदक्षिणा के प्रत्येक चरण में नष्ट हो जाते हैं। मैं यह प्रदक्षिणा पूर्ण निष्ठा और भक्ति के साथ कर रहा हूँ। कृपया मेरे सभी पापों का नाश कर मुझे शुद्ध और मुक्त मार्ग प्रदान करें।",
                    en: "O Divine Lord! All the sins I have committed in this life and in past lives are destroyed with every step of circumambulation. I perform this pradakshina with complete sincerity and devotion. Please eradicate all my sins and grant me a pure and liberated path."
                }
            }
        ]
    },
    {
        id: 43,
        title: {
            hi: "क्षमार्चना मंत्र",
            en: "Forgiveness Seeking Mantra (Kshamarchana Mantra)"
        },
        description: {
            hi: "प्रदक्षिणा के पश्चात अब भगवान शिव से क्षमायाचना की जाती है। यदि पूजा में कोई त्रुटि, भूल या कमी रह गई हो तो भगवान से विनम्रतापूर्वक क्षमा मांगी जाती है।",
            en: "After performing the circumambulation, devotees seek forgiveness from Lord Shiva for any mistakes or omissions made during the worship."
        },
        mantras: [
            {
                mantra: {
                    hi: "करचरणकृतं वा कायजं कर्मजं वा। श्रवण नयनजं वा मानसं वापराधम्॥ विहितमवहितं वा सर्वमेतत् क्षमस्व। जय जय करुणाब्धे श्री महादेव शम्भो॥",
                    en: "Karacaraṇakṛtaṃ Vā Kāyajaṃ Karmajaṃ Vā; Śravaṇa Nayanajaṃ Vā Mānasaṃ Vā Parādham. Vihitam Avahitam Vā Sarvam Etat Kṣamasva; Jaya Jaya Karuṇābdhe Śrī Mahādeva Śambho."
                },
                meaning: {
                    hi: "हे भगवान शिव! जो भी अपराध या भूल हमसे हुई हो — चाहे वह हाथ, पाँव, शरीर, कर्म, श्रवण, दृष्टि या मन से हुई हो — कृपया उन सबको क्षमा करें। हे करुणासागर महादेव! कृपा कर हमें क्षमा करें और कल्याण प्रदान करें। जय जय करुणामयी श्री महादेव शम्भो!",
                    en: "O Lord Shiva! Whatever mistakes or offenses we may have committed — whether through our hands, feet, body, actions, hearing, sight, or mind — please forgive them all. O ocean of compassion, Mahadeva! Forgive us and bless us with Your grace and well-being. Victory to You, O merciful and loving Shri Mahadev Shambho!"
                }
            }
        ]
    },
    {
        id: 44,
        title: {
            hi: "पूजा समर्पण",
            en: "Completion of Worship (Pooja Samarpan)"
        },
        description: {
            hi: "पूजा के अंत में सम्पूर्ण रुद्राभिषेक कर्म भगवान श्री साम्ब सदाशिव को समर्पित किया जाता है, यह किसी व्यक्तिगत इच्छा के लिए नहीं बल्कि भगवान की प्रसन्नता हेतु होता है।" + "\n\n**निर्देश:**\n**हाथ में जल लेकर बोले । ओर फिर जल पृथ्वी पर छोड़ दे**",
            en: "At the conclusion of the worship, the entire Rudrabhishek ritual is dedicated to Lord Shri Samba Sadashiva, not for any personal desire, but solely for the pleasure of the Lord." + "\n**Instructions:**\n**Take water in your hand, recite the mantra, and then release the water onto the earth.**"
        },
        mantras: [
            {
                mantra: {
                    hi: "अनेन कृतेन श्रीरुद्राभिषेककर्मणा श्री भवानीशङ्करमहारुद्रः प्रीयताम्‌, न मम। ॐ श्रीसाम्बसदाशिवार्पणमस्तु॥",
                    en: "Anena Kṛtena Śrīrudrābhiṣekakarmaṇā Śrī Bhavānīśaṅkaramahārudraḥ Prīyatām, Na Mama. Om Śrīsāmbasadāśivārpaṇamastu."
                },
                meaning: {
                    hi: "इस किये गए श्री रुद्राभिषेक कर्म से श्री भवानीशंकर महा रुद्र प्रसन्न हों। यह मेरा व्यक्तिगत उद्देश्य नहीं है, बल्कि यह सम्पूर्ण कर्म भगवान श्री साम्ब सदाशिव को समर्पित है।",
                    en: "May Lord Bhavani-Shankara, the Great Rudra, be pleased with the performance of the Shri Rudrabhishek ritual. This is not for any personal aim, but the entire act is wholly offered to Lord Shri Samba Sadashiva."
                }
            }
        ]
    }
];
// ============================================================================
// KAAL SARP DOSH POOJA STEPS
// ============================================================================
const kaalSarpaDoshaSteps = [
    {
        id: 1,
        title: {
            hi: "स्वस्तिवाचन",
            en: "Swasti Vachan",
        },
        description: {
            hi: "सभी देवताओं से वैदिक मंत्रों के माध्यम से शुभता और सकारात्मकता की प्रार्थना।",
            en: "Praying for auspiciousness and positivity to all the Gods through Vedic mantras.",
        }
    },
    {
        id: 2,
        title: {
            hi: "संकल्प",
            en: "Resolution",
        },
        description: {
            hi: "पूजा के लिए एक संकल्प लें, जिसमें आपका नाम, गोत्र और पूजा का उद्देश्य बताया गया हो।",
            en: "Take a resolution for the puja, which mentions your name, gotra and the purpose of the puja.",
        }
    },
    {
        id: 3,
        title: {
            hi: "श्री गणेश अंबिका आवाहन एवं कलश स्थापना",
            en: "Sri Ganesh Ambika Aavahan and Kalasha Installation",
        },
        description: {
            hi: "बाधाओं को दूर करने के लिए भगवान गणपति और देवी अंबिका का आवाहन। उनका षोडशोपचार पूजन करें और जल से भरा एक कलश स्थापित करें, जिसके ऊपर एक नारियल रखा हो।",
            en: "To remove obstacles, invoke Lord Ganesha and Goddess Ambika. Perform their Shodashopachara puja and place a pot filled with water, topped with a coconut.",
        }
    },
    {
        id: 4,
        title: {
            hi: "षोडशमात्रिका पूजन",
            en: "Shodashmatrika worship",
        },
        description: {
            hi: "देवियों के 16 रूपों की पूजा, उनका आवाहन, ध्यान और कुलदेवी का स्मरण पंचोपचार पूजा के साथ शुभता के लिए करें।",
            en: "Worship the 16 forms of Goddesses, invoke them, meditate on them and remember the Kuldevi with Panchopachar Puja for auspiciousness.",
        }
    },
    {
        id: 5,
        title: {
            hi: "नंदी श्राद्ध",
            en: "Nandi Shraddha",
        },
        description: {
            hi: "पितरों का स्मरण और पूजन करके उनका आशीर्वाद प्राप्त करना।",
            en: "To remember and worship the ancestors and receive their blessings.",
        }
    },
    {
        id: 6,
        title: {
            hi: "नवग्रह पूजन",
            en: "Navagraha Puja",
        },
        description: {
            hi: "सभी नौ ग्रहों (नवग्रहों) को प्रसन्न करने के लिए आवाहन, ध्यान, मंत्र जाप और अर्घ्य।",
            en: "Invocation, meditation, chanting of mantras and offering of arghya to please all the nine planets (Navagrahas).",
        }
    },
    {
        id: 7,
        title: {
            hi: "राहु-केतु शांति",
            en: "Rahu-Ketu peace",
        },
        description: {
            hi: "नवग्रह मंडल के भीतर राहु और केतु के लिए विशेष पूजा और ध्यान।",
            en: "Special worship and meditation for Rahu and Ketu within the Navagraha Mandala.",
        }
    },
    {
        id: 8,
        title: {
            hi: "नव नाग मंडल पूजन",
            en: "Nava Nag Mandal Puja",
        },
        description: {
            hi: "नागों (सर्प देवताओं) का आवाहन और पंचोपचार पूजन।",
            en: "Invocation of Nagas (snake deities) and Panchopachara worship.",
        }
    },
    {
        id: 9,
        title: {
            hi: "रजत नाग-नागिन के जोड़े का पूजन",
            en: "Worship of a pair of silver snakes",
        },
        description: {
            hi: "चांदी के नाग-नागिन की मूर्तियों पर पंचामृत और गंगाजल से अभिषेक करें, उसके बाद पंचोपचार पूजन करें, और दोष के निवारण के लिए प्रार्थना करें।",
            en: "Anoint the silver idols of the snake and the female snake with Panchamrit and Ganga water, then perform Panchopachar puja, and pray for the removal of the defect.",
        }
    },
    {
        id: 10,
        title: {
            hi: "मंत्र जप",
            en: "Chanting mantras",
        },
        description: {
            hi: "राहु और केतु के लिए विशिष्ट मंत्रों का जाप, आमतौर पर 108 या 1100 बार।",
            en: "Chanting specific mantras for Rahu and Ketu, usually 108 or 1100 times.",
        }
    },
    {
        id: 11,
        title: {
            hi: "रुद्राभिषेक एवं नाग स्तुति",
            en: "Rudrabhishekam and Naga stuti",
        },
        description: {
            hi: "भगवान शिव का जल, दूध और बिल्व पत्रों से अभिषेक करें। वेदों से सर्प सूक्त का पाठ किया जाता है। नाग देवताओं से क्षमा और कृपा के लिए प्रार्थना की जाती है।",
            en: "Anoint Lord Shiva with water, milk, and Bilva leaves. The Sarpa Sukta from the Vedas is recited. Prayers are offered to the serpent gods for forgiveness and mercy.",
        }
    },
    {
        id: 12,
        title: {
            hi: "हवन (अत्यंत महत्वपूर्ण)",
            en: "Havan (very important)",
        },
        description: {
            hi: "नागों और नवग्रहों के लिए किया जाने वाला एक अग्नि अनुष्ठान (हवन)।",
            en: "A fire ritual (havan) performed for the Nagas and the Navagrahas.",
        }
    },
    {
        id: 13,
        title: {
            hi: "आरती एवं मंत्रपुष्पंजली",
            en: "Aarti and Mantra Pushpanjali",
        },
        description: {
            hi: "सभी देवताओं के लिए आरती करें।",
            en: "Perform aarti for all the gods.",
        }
    }
];
// ============================================================================
// MANGAL DOSH POOJA STEPS
// ============================================================================
const mangalDoshaSteps = [
    {
        id: 1,
        title: {
            hi: "पवित्रीकरण, भूमि नमस्कार, स्वस्तिवाचन",
            en: "Sanctification, ground salutation, Swastivachan",
        },
        description: {
            hi: "आत्म-शुद्धि, पृथ्वी से प्रार्थना, और वैदिक मंत्रों का श्रवण।",
            en: "Self-purification, prayer to the earth, and listening to Vedic mantras.",
        }
    },
    {
        id: 2,
        title: {
            hi: "संकल्प",
            en: "Resolution",
        },
        description: {
            hi: "पूजा के लिए एक संकल्प लें, जिसमें आपका नाम, गोत्र और दोष को दूर करने का उद्देश्य बताया गया हो।",
            en: "Take a resolution for the puja, mentioning your name, gotra and the purpose of removing the dosha.",
        }
    },
    {
        id: 3,
        title: {
            hi: "श्री गणेश अंबिका आवाहन एवं कलश स्थापना",
            en: "Sri Ganesh Ambika Aavahan and Kalasha Installation",
        },
        description: {
            hi: "बाधाओं को दूर करने के लिए भगवान गणपति, देवी अंबिका और कलश देवता की पूजा।",
            en: "Worship of Lord Ganpati, Goddess Ambika and Kalash deity to remove obstacles.",
        }
    },
    {
        id: 4,
        title: {
            hi: "षोडशमात्रिका पूजन",
            en: "Shodashmatrika worship",
        },
        description: {
            hi: "देवियों के 16 रूपों की पूजा।",
            en: "Worship of 16 forms of goddesses.",
        }
    },
    {
        id: 5,
        title: {
            hi: "नंदी श्राद्ध",
            en: "Nandi Shraddha",
        },
        description: {
            hi: "पितरों का स्मरण और पूजन करके उनका आशीर्वाद प्राप्त करना।",
            en: "To remember and worship the ancestors and receive their blessings.",
        }
    },
    {
        id: 6,
        title: {
            hi: "नवग्रह पूजन",
            en: "Navagraha Puja",
        },
        description: {
            hi: "सभी नौ ग्रहों को प्रसन्न करने के लिए पूजा।",
            en: "Puja to appease all the nine planets.",
        }
    },
    {
        id: 7,
        title: {
            hi: "रुद्रकलश पूजन",
            en: "Rudrakalash worship",
        },
        description: {
            hi: "भगवान शिव का आवाहन एवं पूजन।",
            en: "Invocation and worship of Lord Shiva.",
        }
    },
    {
        id: 8,
        title: {
            hi: "मंगल देवता का विशेष भात पूजन",
            en: "Special Bhaat Puja of Lord Mangal",
        },
        description: {
            hi: "भगवान मंगल नाथ और अंगारेश्वर के पवित्र शिवलिंग का पूजन करना है एवं उन्हें भात अर्पित करना है।",
            en: "The holy Shivalinga of Lord Mangal Nath and Angareshwar has to be worshipped and rice has to be offered to them.",
        }
    },
    {
        id: 9,
        title: {
            hi: "नव गृह हवन",
            en: "New Home Havan",
        },
        description: {
            hi: "नव गृहों की प्रसन्नता प्राप्ति के लिए उनके नाम से हवन करे।",
            en: "To please the nine planets, perform havan in their name.",
        }
    },
    {
        id: 10,
        title: {
            hi: "आरती , मंत्र पुष्पांजलि , क्षमाप्रार्थना",
            en: "Aarti , Mantra Pushpanjali , Prayer of Forgiveness",
        },
        description: {
            hi: "भगवान की आरती करे। दीपक से, कपूर से, निरंजन करे।",
            en: "Perform aarti of God with a lamp, with camphor, with Niranjan.",
        }
    },
    {
        id: 11,
        title: {
            hi: "पूजा का समर्पण",
            en: "dedication to worship",
        },
        description: {
            hi: "पूजा को भगवान को समर्पित करना।",
            en: "Dedicating the puja to God.",
        }
    }
];
// ============================================================================
// TRANSLATIONS
// ============================================================================
export const translations = {
    hi: {
        appSubtitle: "लाइव पूजा में आपका स्वागत है।",
        ongoing: "यह चरण चल रहा है",
        completed: "यह चरण पूर्ण हो चुका है",
        pending: "आगामी चरण",
        adminPanelTitle: "व्यवस्थापक नियंत्रण कक्ष",
        prevButton: "पिछला चरण",
        nextButton: "अगला चरण"
    },
    en: {
        appSubtitle: "Welcome to the live ceremony.",
        ongoing: "This step is ongoing",
        completed: "This step is completed",
        pending: "Upcoming Step",
        adminPanelTitle: "Admin Controls",
        prevButton: "Previous Step",
        nextButton: "Next Step"
    }
};
// ============================================================================
// LANGUAGE OPTIONS
// ============================================================================
export const languageOptions = [
    { code: 'hi', name: 'हिंदी', nativeName: 'Hindi' },
    { code: 'en', name: 'English', nativeName: 'English' }
];
// ============================================================================
// POOJA TYPES CONFIGURATION
// ============================================================================
export const poojaTypes = [
    {
        id: 'rudrabhishek',
        name: {
            hi: 'रूद्राभिषेक पूजा',
            en: 'Rudra Abhishek Pooja',
        },
        description: {
            hi: 'भगवान शिव की पूर्ण अभिषेक पूजा',
            en: 'Complete Shiva Abhishek ceremony',
        },
        duration: '45-60 minutes',
        steps: rudraAbhishekSteps
    },
    {
        id: 'kaalsarpdosh',
        name: {
            hi: 'काल सर्प दोष पूजा',
            en: 'Kaal Sarpa Dosha Pooja',
        },
        description: {
            hi: 'विघ्न निवारण हेतु काल सर्प दोष पूजा',
            en: 'Kaal Sarpa Dosha Pooja for obstacle removal',
        },
        duration: '60-120 minutes',
        steps: kaalSarpaDoshaSteps
    },
    {
        id: 'mangaldosh',
        name: {
            hi: 'मंगल दोष पूजा',
            en: 'Mangal Dosh Pooja',
        },
        description: {
            hi: 'मंगल दोष पूजा',
            en: 'Mangal Dosh Pooja',
        },
        duration: '60-90 minutes',
        steps: mangalDoshaSteps
    }
];
// ============================================================================
// LEGACY EXPORT (for backward compatibility)
// ============================================================================
export const poojaStepsData = rudraAbhishekSteps;
